import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select, { components as SelectComponents } from "react-select";
import Modal from "react-modal";
import moment from "moment";
import remove from "../../../img/remove.png";
import {
  Save,
  CheckCircle,
  CheckCircleOutline,
  AddCircleOutline,
  Close,
  Undo,
  HourglassEmpty,
  Cancel,
  Pending,
  Person,
  DeleteOutline,
  EditNote,
  Refresh,
  ViewColumn,
  ViewList,
  ArrowDropDown,
} from "@mui/icons-material";
import Sidebarmenu from "../../Sidebar/Sidebarmenu";
import Header from "../Header/Header";
import { toast } from "react-toastify";
import loadergif from "../../../img/landing/loader123.gif";
import {
  getThalilist,
  getAllcaterer,
  getCatererGet,
  getDishName,
  createCalender,
  Updatethali,
  getThaliview,
  getCatererSchedule,
  createCatererSchedule,
  updateCatererSchedule,
  deleteCatererSchedule,
  deletethali,
  menu_schedule,
  fmb_food_items_dish,
} from "../../../Redux/Reducer/AuthReducer";
import { HIJRI_MONTH_NAMES, hijriMonthRange, getCurrentHijriYearMonth, getHijriParts } from "../../HijriCalendar/getHijriDate";

// Requested column order for dish-category cells; any dish_type not in this
// list still renders, just appended after these in alpha order,
// so nothing silently disappears from the grid.
const CATEGORY_ORDER = ["Kharas", "Gravy", "Tarkari", "Rice", "Roti", "Salad", "Fruit", "Mithas", "Sherbet"];
// Requested order for the Statistics view's Item Frequency sections - same
// idea as CATEGORY_ORDER above but with Mithas placed between Roti and
// Fruit; any dish_type not in this list still renders, appended after in
// alpha order.
const STATS_CATEGORY_ORDER = ["Kharas", "Gravy", "Tarkari", "Rice", "Roti", "Mithas", "Fruit", "Sherbet"];
const NIYAAZ_TYPE_BY_SCHEDULE = { FMBTHALI: 0, MIQAAT: 1 };
// Caterer master's placeholder "Default Caterer" entry's id - used as the
// fallback Lead Caterer on a brand-new Daily Menu Grid schedule when none is
// already resolved (see handleSaveRow).
const DEFAULT_CATERER_ID = 1;

// Lead Caterer/Team Member/status assignment is edited on the standalone
// Caterer Grid view now; the Daily Menu Grid only shows the resulting
// Caterer read-only, under the Type column.
const SHOW_CATERER_GRID_TOGGLE = true;

// Same name-keyword pattern as UserRegistrationSearch.js - the dish
// master's own allow_skip_rice/allow_skip_roti attribute isn't reliably
// set, so allow-skip on save is derived from the dish name instead.
const isRice = (n) => n.includes("rice") || n.includes("chawal") || n.includes("khichdi");
const isRoti = (n) =>
  // "Tortilla Soup" isn't a roti/bread dish despite containing "tortilla".
  !n.includes("tortilla soup") &&
  (n.includes("roti") ||
    n.includes("naan") ||
    n.includes("tacos") ||
    n.includes("paratha") ||
    n.includes("manda") ||
    n.includes("bread") ||
    n.includes("batura") ||
    n.includes("puri") ||
    n.includes("toast") ||
    n.includes("tortilla") ||
    n.includes("roll") ||
    n.includes("pita") ||
    n.includes("bun") ||
    n.includes("croissant") ||
    n.includes("gakhar") ||
    n.includes("rusk") ||
    n.includes("biscuit") ||
    n.includes("porotta") ||
    n.includes("bagel"));

function parseMenu(menuStr) {
  if (!menuStr) return [];
  try {
    const parsed = JSON.parse(menuStr);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// "YYYY-MM-DD" -> local Date (not new Date(str), which parses date-only
// ISO strings as UTC midnight - in a negative-UTC-offset timezone that can
// read back one calendar day earlier via local getters like getDay()).
function parseDateKeyLocal(dateKeyStr) {
  const [year, month, day] = dateKeyStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

// Same RSVP-date defaults as CalendarPage.js's getRsvpDateForType - FMB
// closes RSVP on the Thursday of the PREVIOUS week (Sun-Sat week), 10:00 AM -
// e.g. a Tue/Wed schedule day goes back to that same short week's Thursday,
// but a Thu/Fri/Sat schedule day goes back a full extra week rather than
// landing on its own week's Thursday. Miqaat closes 3 days before the
// schedule day (Miqaat planning turns around faster, no fixed time).
// Formatted the same way ('MM/DD/YYYY') so both pages save it identically.
function getFmbRsvpDate(scheduleDate) {
  const daysBackToPreviousWeekThursday = scheduleDate.getDay() + 3; // Sun=3 ... Sat=9
  const previousThursday = new Date(scheduleDate);
  previousThursday.setDate(scheduleDate.getDate() - daysBackToPreviousWeekThursday);
  return moment(previousThursday).format("MM/DD/YYYY");
}

function getMiqaatRsvpDate(scheduleDate) {
  const threeDaysBefore = new Date(scheduleDate);
  threeDaysBefore.setDate(scheduleDate.getDate() - 3);
  return moment(threeDaysBefore).format("MM/DD/YYYY");
}

function getRsvpDateForType(scheduleDate, scheduleType) {
  return scheduleType === "MIQAAT" ? getMiqaatRsvpDate(scheduleDate) : getFmbRsvpDate(scheduleDate);
}

function formatDate(date) {
  if (!date) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Shows a small dish-type label (e.g. "Gravy") right-aligned inside the
// select's value area, before its clear/dropdown indicators - so the user
// can tell which column's dropdown they're in even while it's still on its
// placeholder. Passed the category via the custom `dishTypeLabel` prop
// (read back off selectProps, per react-select's components API).
function DishTypeValueContainer({ children, ...props }) {
  return (
    <SelectComponents.ValueContainer {...props}>
      {children}
      {!props.hasValue && props.selectProps.dishTypeLabel && (
        <span className="mp-select-dish-type-badge">{props.selectProps.dishTypeLabel}</span>
      )}
    </SelectComponents.ValueContainer>
  );
}

export default function MenuPlanning() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const access_id = localStorage.getItem("access_id");

  const customModal = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
    },
  };

  // Same dish-click-through as Thaali Schedule's handleNavigateDishPage -
  // Dishes reads location.state.dish_name/schedule_type on mount to
  // pre-filter its search and flip its FMB/Miqaat toggle accordingly.
  const handleNavigateToDish = (dishName, scheduleTypeValue) => {
    navigate("/Dishes", { state: { schedule_type: scheduleTypeValue, dish_name: dishName } });
  };

  const [loading, setLoading] = useState(true);
  // Table-level refresh (Daily Menu Grid / Caterer Grid) - see refreshGridData
  // below. lastRefreshedAt drives the "Last refreshed" label; isManualRefreshing
  // only covers the button-triggered refresh (spins the icon) - the 30s
  // silent auto-refresh never toggles it, so it never shows a spinner/loader.
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [scheduleList, setScheduleList] = useState([]);
  const [dishMaster, setDishMaster] = useState([]);
  const [catererMaster, setCatererMaster] = useState([]);
  // Unfiltered caterer list (admin/caterer-get) - a superset of catererMaster
  // (admin/caterer-get-cook), used only to resolve/display Team Member names,
  // since a Team Member isn't required to be a cook.
  const [catererMasterAll, setCatererMasterAll] = useState([]);
  const [catererScheduleList, setCatererScheduleList] = useState([]); // thaali_schedule_caterer rows
  const [viewMode, setViewMode] = useState("caterer"); // "grid" | "caterer" | "stats-fmb" | "stats-fmb-caterer" | "stats-miqaat" | "stats-miqaat-caterer"
  const [gridEdits, setGridEdits] = useState({}); // rowKey -> { dishSelections: {category: option[]}, leadCaterer, teamMember1, teamMember2, scheduleType, notes, notes2 }
  const [savingRows, setSavingRows] = useState({}); // rowKey -> true while a save is in flight
  const [extraRows, setExtraRows] = useState({}); // dateKey -> rowKey[] of user-added blank rows (via "+" next to Date)
  const [catererGridEdits, setCatererGridEdits] = useState({}); // rowKey -> { scheduleType, leadCaterer, teamMember1/2/3, notes, status }
  const [savingCatererRows, setSavingCatererRows] = useState({});
  const [deletingCatererRows, setDeletingCatererRows] = useState({});
  const [catererRowPendingDelete, setCatererRowPendingDelete] = useState(null); // { rows: {rowKey, row}[], date } | null
  const [deletingThaliMenuIds, setDeletingThaliMenuIds] = useState({});
  const [thaliMenuPendingDelete, setThaliMenuPendingDelete] = useState(null); // { entries: thali_schedule row[], date } | null
  const [extraCatererRows, setExtraCatererRows] = useState({}); // dateKey -> rowKey[]
  // rowKey -> how many Team Member slots are shown in the merged Caterer
  // column (0-2, Lead Caterer is always shown separately) - undefined means
  // "derive from saved data" (see getCatererTeamSlotsShown).
  const [catererTeamSlotsShown, setCatererTeamSlotsShown] = useState({});
  // Clicking a Caterer Grid summary tile filters the rows shown below it -
  // null | "missing" | "assigned" | "PENDING" | "DECLINED" | "CONFIRMED".
  const [catererStatusFilter, setCatererStatusFilter] = useState(null);
  // Same idea, for the Daily Menu Grid's own summary tiles -
  // null | "menuMissing" | "fmb" | "miqaat".
  const [dailyGridFilter, setDailyGridFilter] = useState(null);
  const [dishSearchValue, setDishSearchValue] = useState(null); // global "which type/niyaaz is this dish?" lookup
  const [pastDaysCollapsed, setPastDaysCollapsed] = useState(true); // Daily Menu Grid: hide rows before today, on by default
  // Daily Menu Grid layout: "column" = one column per dish type (original),
  // "grid" = a single Menu column with each dish type as a row inside it.
  // Remembered per user in localStorage so it reopens the way they left it.
  const menuLayoutStorageKey = `mp_daily_menu_layout_${access_id || "default"}`;
  const [menuLayout, setMenuLayout] = useState(() => {
    try {
      return localStorage.getItem(menuLayoutStorageKey) === "grid" ? "grid" : "column";
    } catch {
      return "column";
    }
  });
  const [menuLayoutMenuOpen, setMenuLayoutMenuOpen] = useState(false);
  const menuLayoutDropdownRef = useRef(null);
  const [statsRangeMode, setStatsRangeMode] = useState("monthly"); // Statistics view: "monthly" | "quarterly"
  const nextLocalRowIdRef = useRef(0);
  const nextLocalCatererRowIdRef = useRef(0);
  const gridScrollRef = useRef(null); // the actual scrollable table container
  const gridScrollThumbRef = useRef(null); // custom always-visible scrollbar thumb
  const gridScrollBarRef = useRef(null); // the fixed-to-viewport track wrapper
  const stickyHeaderRef = useRef(null); // title/month-nav/view-toggle block - see stickyHeaderHeight below
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const gridStickyBlockRef = useRef(null); // summary tiles + hint/toolbar block pinned under the page header
  const gridHeadCloneRef = useRef(null); // pinned copy of the grid's header row - see the effect below

  const initialHijri = useMemo(() => getCurrentHijriYearMonth(), []);
  const [hijriYear, setHijriYear] = useState(initialHijri.year);
  const [hijriMonth, setHijriMonth] = useState(initialHijri.month);

  // "Today" only exists within the Hijri month the page loaded on - the
  // Hide/Show Past Days toggle is meaningless (and hidden) on any other month.
  const isCurrentMonth = hijriYear === initialHijri.year && hijriMonth === initialHijri.month;

  // Re-fetch the schedule list only (dish/caterer masters don't change while
  // planning) - used on mount and again after every Save Draft.
  const refetchSchedule = () => {
    return dispatch(getThalilist()).then((res) => {
      if (res?.payload?.status === true) {
        setScheduleList(res.payload.data || []);
      } else {
        toast.error(res?.payload?.message || "Failed to load schedule data");
      }
    });
  };

  // Fetched regardless of which view is active - the Daily Menu Grid needs
  // to know about caterer-schedule assignments too (to prefill/lock its own
  // Caterer column), not just the Caterer Grid view itself.
  const refetchCatererSchedule = () => {
    return dispatch(getCatererSchedule()).then((res) => {
      if (res?.payload?.status === true) {
        setCatererScheduleList(res.payload.data || []);
      } else {
        toast.error(res?.payload?.message || "Failed to load caterer schedule data");
      }
    });
  };

  // Table-level refresh for the Daily Menu Grid / Caterer Grid - re-pulls
  // scheduleList + catererScheduleList (both grids read from both: the
  // Caterer Grid also merges in plain thaali_schedule rows, and the Daily
  // Menu Grid shows the caterer assignment read-only) without touching
  // gridEdits/catererGridEdits/extraRows/extraCatererRows, so anything the
  // user has picked/typed but not yet saved survives - those are separate
  // state, only ever layered on top of scheduleList/catererScheduleList by
  // getEffectiveRow/getEffectiveCatererRow, never overwritten by them.
  // silent=true (the 30s auto-refresh below) skips the spinner so it's
  // invisible; a manual click spins the refresh icon while in flight.
  const refreshGridData = (silent = false) => {
    if (!silent) setIsManualRefreshing(true);
    return Promise.all([refetchSchedule(), refetchCatererSchedule()])
      .then(() => setLastRefreshedAt(new Date()))
      .finally(() => {
        if (!silent) setIsManualRefreshing(false);
      });
  };

  // Silent auto-refresh every 30s - refreshGridData's own edit-state
  // isolation (see above) is what makes this safe to run unconditionally in
  // the background, regardless of which view is showing or what the user is
  // mid-edit on.
  useEffect(() => {
    const interval = setInterval(() => {
      refreshGridData(true);
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch both dish lists and re-tag them the same way the initial mount
  // effect does - used after a drag-and-drop dish_type move (see
  // handleDropDishOnCategory) so the moved dish's card membership updates
  // to reflect what was actually saved, not just an optimistic local edit.
  const refetchDishMaster = () => {
    return Promise.all([dispatch(getDishName(0)), dispatch(getDishName(1))]).then(
      ([fmbDishRes, niyaazDishRes]) => {
        const fmbDishes = (fmbDishRes?.payload?.status === true ? fmbDishRes.payload.data || [] : []).map((d) => ({
          ...d,
          _niyaazType: 0,
        }));
        const niyaazDishes = (niyaazDishRes?.payload?.status === true ? niyaazDishRes.payload.data || [] : []).map((d) => ({
          ...d,
          _niyaazType: 1,
        }));
        setDishMaster([...fmbDishes, ...niyaazDishes]);
      }
    );
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dispatch(getThalilist()),
      dispatch(getAllcaterer()),
      dispatch(getCatererGet()),
      dispatch(getDishName(0)), // FMBTHALI dishes
      dispatch(getDishName(1)), // Niyaaz dishes
      dispatch(getCatererSchedule()),
    ])
      .then(([scheduleRes, catererRes, catererAllRes, fmbDishRes, niyaazDishRes, catererScheduleRes]) => {
        if (scheduleRes?.payload?.status === true) {
          setScheduleList(scheduleRes.payload.data || []);
        } else {
          toast.error(scheduleRes?.payload?.message || "Failed to load schedule data");
        }
        if (catererRes?.payload?.status === true) {
          setCatererMaster(catererRes.payload.data || []);
        }
        if (catererAllRes?.payload?.status === true) {
          setCatererMasterAll(catererAllRes.payload.data || []);
        }
        if (catererScheduleRes?.payload?.status === true) {
          setCatererScheduleList(catererScheduleRes.payload.data || []);
        }
        // Tag each dish with which list it came from (0 = FMB Thaali, 1 = Miqaat/Niyaaz)
        // so dropdowns can be filtered per-row by that row's schedule type.
        const fmbDishes = (fmbDishRes?.payload?.status === true ? fmbDishRes.payload.data || [] : []).map((d) => ({
          ...d,
          _niyaazType: 0,
        }));
        const niyaazDishes = (niyaazDishRes?.payload?.status === true ? niyaazDishRes.payload.data || [] : []).map((d) => ({
          ...d,
          _niyaazType: 1,
        }));
        setDishMaster([...fmbDishes, ...niyaazDishes]);
        setLastRefreshedAt(new Date());
      })
      .catch(() => toast.error("Failed to load menu planning data"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Edits are per-month scratch state - drop them when the visible month changes.
  // Also reset the past-days toggle back to its default (hidden) - the button
  // itself is only shown on the current month anyway, so this mainly matters
  // when navigating back to it.
  useEffect(() => {
    setGridEdits({});
    setExtraRows({});
    setCatererGridEdits({});
    setExtraCatererRows({});
    setCatererTeamSlotsShown({});
    setPastDaysCollapsed(true);
  }, [hijriYear, hijriMonth]);

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => hijriMonthRange(hijriYear, hijriMonth),
    [hijriYear, hijriMonth]
  );

  // Statistics view range: a single Hijri month, or a rolling 3-month
  // ("quarterly") window anchored at hijriYear/hijriMonth when that toggle
  // is on. The Daily Menu Grid always stays single-month (rangeStart/rangeEnd
  // above) regardless of this.
  const { start: statsRangeStart, end: statsRangeEnd } = useMemo(() => {
    if (statsRangeMode !== "quarterly") return { start: rangeStart, end: rangeEnd };
    const quarterEndMonth = (hijriMonth + 2) % 12;
    const quarterEndYear = hijriYear + Math.floor((hijriMonth + 2) / 12);
    const { end } = hijriMonthRange(quarterEndYear, quarterEndMonth);
    return { start: rangeStart, end };
  }, [statsRangeMode, rangeStart, rangeEnd, hijriYear, hijriMonth]);

  function filterEntriesByRange(list, start, end) {
    const startTime = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const endTime = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).getTime();
    return list.filter((item) => {
      if (!item?.schedule_day) return false;
      const t = new Date(item.schedule_day).getTime();
      return !Number.isNaN(t) && t >= startTime && t <= endTime;
    });
  }

  // Single Hijri month - drives the Daily Menu Grid (always one month at a time).
  const monthEntries = useMemo(
    () => filterEntriesByRange(scheduleList, rangeStart, rangeEnd),
    [scheduleList, rangeStart, rangeEnd]
  );

  // Statistics view range - same as monthEntries unless quarterly mode is on.
  const statsEntries = useMemo(
    () => filterEntriesByRange(scheduleList, statsRangeStart, statsRangeEnd),
    [scheduleList, statsRangeStart, statsRangeEnd]
  );

  const DAY_MS = 24 * 60 * 60 * 1000;

  // Dish/caterer occurrence counts, trailing from the date the user is
  // actually scheduling - "last 30 days" and "last 90 days" ending on (and
  // including) that day, not tied to whatever Hijri month/quarter happens to
  // be showing. Memoized per scheduleList version, then cached per dateKey
  // so multiple rows on the same day (or repeated renders) don't re-scan.
  const occurrenceCountsByDate = useMemo(() => {
    const cache = {};
    return (dateKey) => {
      if (cache[dateKey]) return cache[dateKey];
      const refTime = new Date(dateKey).getTime();
      const monthlyStart = refTime - 29 * DAY_MS;
      const quarterlyStart = refTime - 89 * DAY_MS;
      const dishMonthly = {};
      const dishQuarterly = {};
      const catererMonthly = {};
      const catererQuarterly = {};
      scheduleList.forEach((item) => {
        if (!item?.schedule_day) return;
        const t = new Date(item.schedule_day).getTime();
        if (Number.isNaN(t) || t > refTime || t < quarterlyStart) return;
        const inMonthly = t >= monthlyStart;
        parseMenu(item.menu).forEach((dishName) => {
          if (!dishName || dishName === "-") return;
          const key = dishName.trim().toLowerCase();
          dishQuarterly[key] = (dishQuarterly[key] || 0) + 1;
          if (inMonthly) dishMonthly[key] = (dishMonthly[key] || 0) + 1;
        });
        [item.cater_name1, item.cater_name2].forEach((name) => {
          if (!name || name === "null" || name === "NA") return;
          const key = name.trim().toLowerCase();
          catererQuarterly[key] = (catererQuarterly[key] || 0) + 1;
          if (inMonthly) catererMonthly[key] = (catererMonthly[key] || 0) + 1;
        });
      });
      const result = { dishMonthly, dishQuarterly, catererMonthly, catererQuarterly };
      cache[dateKey] = result;
      return result;
    };
  }, [scheduleList]);

  // dateKey defaults to today - used by the global dish search, which isn't
  // tied to any particular row/date.
  function getDishCounts(dishName, dateKey = todayKey) {
    const { dishMonthly, dishQuarterly } = occurrenceCountsByDate(dateKey);
    const key = dishName?.trim().toLowerCase();
    return {
      monthlyCount: (key && dishMonthly[key]) || 0,
      quarterlyCount: (key && dishQuarterly[key]) || 0,
    };
  }

  function getCatererCounts(catererName, dateKey = todayKey) {
    const { catererMonthly, catererQuarterly } = occurrenceCountsByDate(dateKey);
    const key = catererName?.trim().toLowerCase();
    return {
      monthlyCount: (key && catererMonthly[key]) || 0,
      quarterlyCount: (key && catererQuarterly[key]) || 0,
    };
  }

  const dishTypeByName = useMemo(() => {
    const map = {};
    dishMaster.forEach((d) => {
      if (d?.dish_name) {
        map[d.dish_name.trim().toLowerCase()] = d.dish_type || "Other";
      }
    });
    return map;
  }, [dishMaster]);

  const summary = useMemo(() => {
    let thaaliEntries = 0;
    let miqaatEntries = 0;
    const uniqueDays = new Set();
    statsEntries.forEach((item) => {
      uniqueDays.add(item.schedule_day);
      if (item.schedule_type === "MIQAAT") miqaatEntries++;
      else thaaliEntries++;
    });
    return { uniqueDays: uniqueDays.size, thaaliEntries, miqaatEntries };
  }, [statsEntries]);

  // Builds one niyaaz type's (0 = FMB, 1 = Miqaat) Item Frequency, grouped
  // and ordered by STATS_CATEGORY_ORDER. Every dish in dishMaster for that
  // type is seeded at count 0 first, so dishes that never occurred in the
  // period still show up (at 0) rather than silently disappearing - any
  // dish that DID occur but has since been removed from dishMaster still
  // shows too, since occurrences are layered in independently of the seed.
  function buildItemFrequency(niyaazType) {
    const byCategory = {};
    dishMaster.forEach((d) => {
      if ((d?._niyaazType ?? 0) !== niyaazType || !d?.dish_name || !d?.dish_type) return;
      const key = d.dish_name.trim().toLowerCase();
      if (!byCategory[d.dish_type]) byCategory[d.dish_type] = {};
      if (!byCategory[d.dish_type][key]) {
        byCategory[d.dish_type][key] = { name: d.dish_name, count: 0, last: null };
      }
    });
    statsEntries
      .filter((item) => (NIYAAZ_TYPE_BY_SCHEDULE[item.schedule_type] ?? 0) === niyaazType)
      .forEach((item) => {
        const dishes = parseMenu(item.menu);
        const day = item.schedule_day ? new Date(item.schedule_day) : null;
        dishes.forEach((dishName) => {
          if (!dishName || dishName === "-") return;
          const key = dishName.trim().toLowerCase();
          const category = dishTypeByName[key] || "Other";
          if (!byCategory[category]) byCategory[category] = {};
          if (!byCategory[category][key]) byCategory[category][key] = { name: dishName, count: 0, last: null };
          const entry = byCategory[category][key];
          entry.count += 1;
          if (day && !Number.isNaN(day.getTime()) && (!entry.last || day > entry.last)) {
            entry.last = day;
          }
        });
      });
    // "Other" is forced last regardless of where it'd otherwise alphabetize.
    const orderIndex = (name) => {
      if (name === "Other") return STATS_CATEGORY_ORDER.length + 1;
      const idx = STATS_CATEGORY_ORDER.indexOf(name);
      return idx === -1 ? STATS_CATEGORY_ORDER.length : idx;
    };
    return Object.keys(byCategory)
      .sort((a, b) => {
        const diff = orderIndex(a) - orderIndex(b);
        return diff !== 0 ? diff : a.localeCompare(b);
      })
      .map((category) => ({
        category,
        dishes: Object.values(byCategory[category]).sort((a, b) => b.count - a.count),
      }));
  }

  const fmbItemFrequency = useMemo(
    () => buildItemFrequency(0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statsEntries, dishTypeByName, dishMaster]
  );
  const miqaatItemFrequency = useMemo(
    () => buildItemFrequency(1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statsEntries, dishTypeByName, dishMaster]
  );

  // Builds one niyaaz type's (0 = FMB, 1 = Miqaat) Caterer Frequency, same
  // shape/zero-fill rationale as buildItemFrequency above.
  function buildCatererFrequency(niyaazType) {
    const counts = {};
    statsEntries
      .filter((item) => (NIYAAZ_TYPE_BY_SCHEDULE[item.schedule_type] ?? 0) === niyaazType)
      .forEach((item) => {
        const day = item.schedule_day ? new Date(item.schedule_day) : null;
        [item.cater_name1, item.cater_name2].forEach((name) => {
          if (!name || name === "null" || name === "NA") return;
          if (!counts[name]) counts[name] = { count: 0, last: null };
          counts[name].count += 1;
          if (day && !Number.isNaN(day.getTime()) && (!counts[name].last || day > counts[name].last)) {
            counts[name].last = day;
          }
        });
      });
    // Include zero-count caterers from the master list, matching the reference
    // spreadsheet's Caterer Frequency sheet (every caterer listed, even at 0).
    catererMaster.forEach((c) => {
      const name = `${c?.first_name || ""} ${c?.last_name || ""}`.trim();
      if (name && !counts[name]) {
        counts[name] = { count: 0, last: null };
      }
    });
    return Object.entries(counts)
      .map(([name, stat]) => ({ name, ...stat }))
      .sort((a, b) => b.count - a.count);
  }

  const fmbCatererFrequency = useMemo(
    () => buildCatererFrequency(0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statsEntries, catererMaster]
  );
  const miqaatCatererFrequency = useMemo(
    () => buildCatererFrequency(1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statsEntries, catererMaster]
  );

  // ---- Daily Menu Grid ----

  function toDateKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  const todayKey = useMemo(() => toDateKey(new Date()), []);

  const handleMenuLayoutChange = (layout) => {
    setMenuLayout(layout);
    setMenuLayoutMenuOpen(false);
    try {
      localStorage.setItem(menuLayoutStorageKey, layout);
    } catch {
      // storage unavailable - the choice still applies for this session
    }
  };

  // Close the Column/Grid view dropdown on any click outside it.
  useEffect(() => {
    if (!menuLayoutMenuOpen) return undefined;
    const handleOutsideClick = (e) => {
      if (menuLayoutDropdownRef.current && !menuLayoutDropdownRef.current.contains(e.target)) {
        setMenuLayoutMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [menuLayoutMenuOpen]);

  // The grid's dropdowns live inside a horizontally-scrollable table
  // (overflow-x: auto), which clips the select menu popup unless it's
  // portaled straight to <body> - otherwise the option list is invisible/
  // unclickable on most rows.
  const selectMenuPortalProps = {
    menuPortalTarget: document.body,
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
  };

  // Lead Caterer/Team Member menus additionally get a minimum menu width -
  // the option row (see formatOptionLabelWithCounts) packs a name next to
  // "Monthly: X - Quarterly: Y", and the Caterer column's cell is narrow
  // enough that, without this, the name has almost no room and truncates
  // down to a single letter.
  const catererSelectPortalProps = {
    ...selectMenuPortalProps,
    styles: {
      ...selectMenuPortalProps.styles,
      menu: (base) => ({ ...base, minWidth: 260 }),
    },
  };

  // Daily Menu Grid's dish columns are now narrow (names wrap - see
  // Commonstyle.css), so their option list needs the same minimum menu
  // width to keep the "Monthly / Quarterly" counts readable.
  const dishSelectPortalProps = catererSelectPortalProps;

  const categories = useMemo(() => {
    const set = new Set();
    dishMaster.forEach((d) => {
      if (d?.dish_type) set.add(d.dish_type);
    });
    const orderIndex = (name) => {
      const idx = CATEGORY_ORDER.indexOf(name);
      return idx === -1 ? CATEGORY_ORDER.length : idx;
    };
    return Array.from(set).sort((a, b) => {
      const diff = orderIndex(a) - orderIndex(b);
      return diff !== 0 ? diff : a.localeCompare(b);
    });
  }, [dishMaster]);

  // Read-only lookup for the "which category/type is this dish under?" search
  // box - not wired to any grid cell, just helps the user find the right
  // column before picking the dish from that row's own dropdown.
  const dishSearchOptions = useMemo(() => {
    return dishMaster
      .filter((d) => d?.dish_name && d?.dish_type)
      .map((d) => ({
        value: d.id,
        label: d.dish_name,
        dishType: d.dish_type,
        niyaazType: d._niyaazType,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dishMaster]);

  const formatDishSearchOption = (option) => {
    const { monthlyCount, quarterlyCount } = getDishCounts(option.label);
    return (
      <div className="mp-dish-search-option">
        <div className="mp-dish-search-option-name">{option.label} - {option.dishType}</div>
        <div className="mp-dish-search-option-niyaaz">
          {option.niyaazType === 1 ? "Miqaat" : "FMB"}
          <span className="mp-dish-occurrence-counts">Monthly: {monthlyCount} &middot; Quarterly: {quarterlyCount}</span>
        </div>
      </div>
    );
  };

  // Dropdown-list-only label (formatOptionLabel's `context` param is "menu"
  // for options in the open list, "value" for the currently selected item) -
  // keeps the persisted selected-value display clean (just the dish name)
  // while the open list shows how often the dish has recently been used.
  // Shared by both the dish and caterer pickers below.
  const formatOptionLabelWithCounts = (option, { context }) => {
    if (context !== "menu") return option.label;
    return (
      <div className="mp-dish-pick-option">
        <span className="mp-dish-pick-option-name">{option.label}</span>
        <span className="mp-dish-occurrence-counts">Monthly: {option.monthlyCount || 0} &middot; Quarterly: {option.quarterlyCount || 0}</span>
      </div>
    );
  };

  // Keyed by niyaaz type (0 = FMB, 1 = Miqaat) so an existing entry's dish
  // names resolve to the dish from the correct list.
  const dishByNameAndType = useMemo(() => {
    const map = { 0: {}, 1: {} };
    dishMaster.forEach((d) => {
      if (d?.dish_name && (d._niyaazType === 0 || d._niyaazType === 1)) {
        map[d._niyaazType][d.dish_name.trim().toLowerCase()] = d;
      }
    });
    return map;
  }, [dishMaster]);

  // ---- FMB/Miqaat Statistics: drag a dish onto another category card to
  // move it there (updates dish_type). Native HTML5 drag-and-drop - no
  // library in this project for it, and a handful of category cards with a
  // few dozen dishes each doesn't need one. ----
  const [draggedDish, setDraggedDish] = useState(null); // { name, category, niyaazType } | null
  const [dragOverCategory, setDragOverCategory] = useState(null);
  const [movingDishName, setMovingDishName] = useState(null); // dish name currently being saved, or null

  const handleDishDragStart = (e, dishName, category, niyaazType) => {
    setDraggedDish({ name: dishName, category, niyaazType });
    e.dataTransfer.effectAllowed = "move";
    // Required by Firefox for the drag to initiate at all - the actual
    // payload isn't read back from this, drop handling uses draggedDish.
    e.dataTransfer.setData("text/plain", dishName);
  };

  const handleDishDragEnd = () => {
    setDraggedDish(null);
    setDragOverCategory(null);
  };

  const handleCategoryDragOver = (e, category) => {
    if (!draggedDish || draggedDish.category === category) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverCategory !== category) setDragOverCategory(category);
  };

  const handleCategoryDragLeave = (category) => {
    setDragOverCategory((prev) => (prev === category ? null : prev));
  };

  // The dish-name-list endpoint dishMaster is built from doesn't carry the
  // full dish record (no ingredients/caterer_id/etc.), and admin/dish/update
  // expects the full record back or it risks nulling out whatever's missing
  // - so this fetches the current full record first (same admin/dish-get
  // call Dishes.js's own edit form uses), patches just dish_type, and PUTs
  // it back the same way Dishes.js's handleSubmitUpdate does.
  const moveDishToCategory = (dish, targetCategory) => {
    setMovingDishName(dish.dish_name);
    fetch(menu_schedule + "admin/dish-get", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("access_token"),
      },
      body: JSON.stringify({ dish_id: dish.id, caterer_id: "" }),
    })
      .then((res) => res.json())
      .then((data) => {
        const full = data?.success === true ? data?.data || data?.payLoad : null;
        if (!full) {
          toast.error(data?.message || "Failed to load dish before moving it");
          return null;
        }
        const ingredientPayload = (full.ingredients || []).map((ing, i) => ({
          index: i,
          ingredient_id: ing.ingredient_id,
          quantity: ing.quantity,
          unit: ing.unit,
        }));
        const postData = {
          dish_name: full.dish_name,
          dish_type: targetCategory,
          is_active: 1,
          caterer_id: full.caterer_id,
          allow_skip_rice: full.allow_skip_rice,
          dish_id: full.dish_id ?? full.dish_ids ?? dish.id,
          allow_skip_roti: full.allow_skip_roti,
          cutting_instructions: "1",
          general_instructions: "1",
          quantity_updated_from: "1",
          dish_note: full.dish_note,
          dish_packaging_type: full.dish_packaging_type,
          x_small_multi: full.x_small_multi,
          one_two_multi: full.one_two_multi,
          three_four_multi: full.three_four_multi,
          five_six_multi: full.five_six_multi,
          created_user: access_id,
          updated_user: access_id,
          source: "web",
          ingredient: JSON.stringify(ingredientPayload),
          niyaz_type_description: full.niyaz_type_description,
          niyaaz_type: full.niyaaz_type,
          is_validated: full.is_validated,
          validated_by: full.is_validated == "1" ? access_id : null,
          validated_at: full.is_validated == "1" ? new Date().toISOString() : null,
        };
        return fetch(fmb_food_items_dish + "admin/dish/update", {
          method: "PUT",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("access_token"),
          },
          body: JSON.stringify(postData),
        }).then((res) => res.json());
      })
      .then((res) => {
        if (!res) return;
        if (res?.status === true) {
          toast.success(res?.message || `Moved "${dish.dish_name}" to ${targetCategory}`);
          refetchDishMaster();
        } else {
          toast.error(res?.message || "Failed to move dish");
        }
      })
      .catch(() => toast.error("Failed to move dish"))
      .finally(() => setMovingDishName(null));
  };

  const handleDropDishOnCategory = (e, targetCategory, niyaazType) => {
    e.preventDefault();
    setDragOverCategory(null);
    if (!draggedDish || draggedDish.niyaazType !== niyaazType || draggedDish.category === targetCategory) {
      setDraggedDish(null);
      return;
    }
    const dish = dishByNameAndType[niyaazType]?.[draggedDish.name.trim().toLowerCase()];
    setDraggedDish(null);
    if (!dish) {
      toast.error("Couldn't find that dish to move it");
      return;
    }
    moveDishToCategory(dish, targetCategory);
  };

  const dishOptionsByCategoryAndType = useMemo(() => {
    const byType = { 0: {}, 1: {} };
    dishMaster.forEach((d) => {
      if (!d?.dish_type || (d._niyaazType !== 0 && d._niyaazType !== 1)) return;
      const byCategory = byType[d._niyaazType];
      if (!byCategory[d.dish_type]) byCategory[d.dish_type] = [];
      byCategory[d.dish_type].push({ value: d.id, label: d.dish_name });
    });
    Object.values(byType).forEach((byCategory) => {
      Object.keys(byCategory).forEach((c) => byCategory[c].sort((a, b) => a.label.localeCompare(b.label)));
    });
    return byType;
  }, [dishMaster]);

  // FMB rows only see FMB dishes, Miqaat rows only see Miqaat dishes.
  // Enriched with last-30/90-day occurrence counts (relative to the row's
  // own date) on every call rather than baked into the memo above, since
  // counts change far more often (any schedule edit, or just which row this
  // is) than the dish master list itself.
  const getDishOptions = (category, scheduleTypeValue, dateKey) => {
    const niyaazType = NIYAAZ_TYPE_BY_SCHEDULE[scheduleTypeValue] ?? 0;
    const options = dishOptionsByCategoryAndType[niyaazType]?.[category] || [];
    return options.map((option) => ({ ...option, ...getDishCounts(option.label, dateKey) }));
  };

  const catererOptions = useMemo(
    () =>
      catererMaster
        .map((c) => ({
          value: c.id,
          label: `${c?.first_name || ""} ${c?.last_name || ""}`.trim(),
        }))
        .filter((o) => o.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [catererMaster]
  );

  // Enriched with last-30/90-day occurrence counts relative to the row's own
  // date, same rationale as getDishOptions above. When defaultCatererId is
  // given (the Caterer Grid's Lead Caterer dropdown passes the placeholder
  // "Default Caterer" entry's id - see defaultCatererId below), that option
  // is sorted to the top so it's the first thing the user sees.
  const getCatererOptions = (dateKey, defaultCatererId) => {
    const options = catererOptions.map((option) => ({ ...option, ...getCatererCounts(option.label, dateKey) }));
    if (defaultCatererId == null) return options;
    return [...options].sort((a, b) => {
      const aIsDefault = String(a.value) === String(defaultCatererId);
      const bIsDefault = String(b.value) === String(defaultCatererId);
      if (aIsDefault === bIsDefault) return 0;
      return aIsDefault ? -1 : 1;
    });
  };

  // catererMaster (admin/caterer-get-cook) plus anything admin/caterer-get
  // adds on top, deduped by id - Team Member options/name-resolution use
  // this superset instead of catererMaster directly, since a Team Member
  // isn't required to be a cook the way a Lead Caterer is.
  const catererMasterMerged = useMemo(() => {
    const byId = new Map();
    catererMaster.forEach((c) => byId.set(String(c.id), c));
    catererMasterAll.forEach((c) => byId.set(String(c.id), c));
    return Array.from(byId.values());
  }, [catererMaster, catererMasterAll]);

  const teamMemberCatererOptions = useMemo(
    () =>
      catererMasterMerged
        .map((c) => ({
          value: c.id,
          label: `${c?.first_name || ""} ${c?.last_name || ""}`.trim(),
        }))
        .filter((o) => o.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [catererMasterMerged]
  );

  const catererIdByName = useMemo(() => {
    const map = {};
    catererMaster.forEach((c) => {
      const name = `${c?.first_name || ""} ${c?.last_name || ""}`.trim();
      if (name) map[name.trim().toLowerCase()] = c.id;
    });
    return map;
  }, [catererMaster]);

  // "Default Caterer" is a specific placeholder caterer in the master list
  // (name contains "default", e.g. "Default Caterer") - the same one
  // everywhere, not tied to any particular day. Used both to sort it first
  // in the Lead Caterer dropdown and, in the other direction, as what
  // marking a row Open resets Lead Caterer back to (see
  // handleCatererMarkOpen) - Open is a manual-only status now, never
  // auto-derived from picking this caterer (see handleCatererLeadChange).
  // Declared here (rather than nearer those call sites) so it's available
  // before catererGridSummary's useMemo below, which also needs it.
  const defaultCatererId = useMemo(() => {
    const match = catererMaster.find((c) =>
      `${c?.first_name || ""} ${c?.last_name || ""}`.trim().toLowerCase().includes("default")
    );
    return match ? match.id : null;
  }, [catererMaster]);

  const gridDays = useMemo(() => {
    const days = [];
    const cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
    const last = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate());
    while (cursor.getTime() <= last.getTime()) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [rangeStart, rangeEnd]);

  // "Hide Past Days" - drop rows before today so Today's row lands at the
  // top of the table. If the whole visible month is already in the past
  // (nothing left to show), fall back to the full list instead of hiding
  // every row.
  const visibleGridDays = useMemo(() => {
    if (!pastDaysCollapsed) return gridDays;
    const filtered = gridDays.filter((date) => toDateKey(date) >= todayKey);
    return filtered.length > 0 ? filtered : gridDays;
  }, [gridDays, pastDaysCollapsed, todayKey]);

  // Custom always-visible scrollbar for the Daily Menu Grid - the native
  // one is hidden via CSS since macOS's default overlay-scrollbar setting
  // makes it only appear during an active scroll gesture, which this whole
  // feature exists to fix. Thumb width/position is kept in sync with the
  // real scroll state imperatively (not via React state) so it doesn't
  // re-render the grid on every scroll tick.
  const updateScrollThumb = () => {
    const container = gridScrollRef.current;
    const thumb = gridScrollThumbRef.current;
    if (!container || !thumb) return;
    const { scrollWidth, clientWidth, scrollLeft } = container;
    if (scrollWidth <= clientWidth) {
      thumb.style.width = "100%";
      thumb.style.left = "0%";
      return;
    }
    const widthPct = (clientWidth / scrollWidth) * 100;
    const leftPct = (scrollLeft / (scrollWidth - clientWidth)) * (100 - widthPct);
    thumb.style.width = `${widthPct}%`;
    thumb.style.left = `${leftPct}%`;
  };

  // The scrollbar track is position:fixed to the bottom of the viewport (so
  // it stays visible while the page itself scrolls up/down), which means it
  // has to be told its left/width explicitly rather than inheriting them
  // from normal document flow - kept aligned with the real scrollable
  // table's own horizontal position/width (which shifts with the sidebar).
  const updateScrollBarPosition = () => {
    const container = gridScrollRef.current;
    const bar = gridScrollBarRef.current;
    if (!container || !bar) return;
    const rect = container.getBoundingClientRect();
    bar.style.left = `${rect.left}px`;
    bar.style.width = `${rect.width}px`;
  };

  // Every sticky column after the first (Save, fixed at left:0) needs its
  // `left` MEASURED off its actual predecessors' rendered widths rather than
  // assumed from CSS min-width - table-layout:auto only treats width/
  // min-width as advisory, so the browser can (and does) stretch a column
  // wider than declared when redistributing the table's own forced
  // min-width slack, which would otherwise throw the offset off. Both grids
  // now freeze the same Day, Date, Type order.
  const updateStickyColumnOffset = () => {
    const container = gridScrollRef.current;
    if (!container) return;
    const table = container.querySelector("table");
    if (!table) return;
    const stickyClasses = [".mp-grid-day-col", ".mp-grid-date-col", ".mp-grid-type-col"];
    const saveIconCol = container.querySelector(".mp-grid-save-icon-col");
    const firstCol = container.querySelector(stickyClasses[0]);
    if (!saveIconCol || !firstCol) return;
    // The first sticky column after Save sits at a static CSS `left` (its
    // own fallback), so only columns from the second one onward need their
    // offset measured and set here.
    let offset = saveIconCol.getBoundingClientRect().width + firstCol.getBoundingClientRect().width;
    for (let i = 1; i < stickyClasses.length; i += 1) {
      const col = container.querySelector(stickyClasses[i]);
      if (!col) return;
      container.querySelectorAll(stickyClasses[i]).forEach((el) => {
        el.style.left = `${offset}px`;
      });
      offset += col.getBoundingClientRect().width;
    }
  };

  useEffect(() => {
    const container = gridScrollRef.current;
    if (!container) return undefined;
    updateScrollThumb();
    updateScrollBarPosition();
    updateStickyColumnOffset();
    container.addEventListener("scroll", updateScrollThumb);
    window.addEventListener("resize", updateScrollBarPosition);
    const resizeObserver = new ResizeObserver(() => {
      updateScrollThumb();
      updateScrollBarPosition();
      updateStickyColumnOffset();
    });
    resizeObserver.observe(container);
    return () => {
      container.removeEventListener("scroll", updateScrollThumb);
      window.removeEventListener("resize", updateScrollBarPosition);
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, visibleGridDays, categories]);

  // Keeps the grid's header row on screen while the page scrolls. The real
  // <thead> can't be position:sticky against the page - it sits inside
  // .mp-grid-scroll (overflow-x: auto), which captures sticky - and moving
  // it with JS on scroll lagged a frame and flickered. So a copy of the
  // header is laid over the real one in .mp-grid-head-clone, which IS
  // sticky against the page (pinned right under .mp-grid-sticky-block, no
  // JS on vertical scroll at all). Its column widths are copied from the
  // real table and its scrollLeft follows the grid's horizontal scroll.
  useEffect(() => {
    const container = gridScrollRef.current;
    const headWrap = gridHeadCloneRef.current;
    const block = gridStickyBlockRef.current;
    if (!container || !headWrap || !block) return undefined;
    let frame = null;
    const build = () => {
      frame = null;
      const table = container.querySelector("table");
      const thead = table?.querySelector("thead");
      if (!table || !thead) return;
      const ths = Array.from(thead.querySelectorAll("th"));
      const tableRect = table.getBoundingClientRect();
      const lefts = ths.map((th) => th.getBoundingClientRect().left);
      const colgroup = document.createElement("colgroup");
      lefts.forEach((left, idx) => {
        const right = idx + 1 < lefts.length ? lefts[idx + 1] : tableRect.right;
        const col = document.createElement("col");
        col.style.width = `${right - left}px`;
        colgroup.appendChild(col);
      });
      const cloneTable = document.createElement("table");
      cloneTable.className = table.className;
      cloneTable.style.width = `${tableRect.width}px`;
      cloneTable.style.minWidth = `${tableRect.width}px`;
      cloneTable.style.tableLayout = "fixed";
      cloneTable.appendChild(colgroup);
      cloneTable.appendChild(thead.cloneNode(true));
      headWrap.replaceChildren(cloneTable);
      const headHeight = thead.getBoundingClientRect().height;
      headWrap.style.height = `${headHeight}px`;
      headWrap.style.marginBottom = `-${headHeight}px`;
      headWrap.style.top = `${stickyHeaderHeight + block.getBoundingClientRect().height}px`;
      headWrap.scrollLeft = container.scrollLeft;
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(build);
    };
    const syncScroll = () => {
      headWrap.scrollLeft = container.scrollLeft;
    };
    build();
    container.addEventListener("scroll", syncScroll);
    window.addEventListener("resize", schedule);
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(block);
    const table = container.querySelector("table");
    if (table) resizeObserver.observe(table);
    const thead = table?.querySelector("thead");
    if (thead) resizeObserver.observe(thead);
    return () => {
      container.removeEventListener("scroll", syncScroll);
      window.removeEventListener("resize", schedule);
      resizeObserver.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [viewMode, visibleGridDays, categories, menuLayout, stickyHeaderHeight, loading]);

  // Tracks the sticky title/month-nav/view-toggle block's actual rendered
  // height so the Days Shown/... summary tiles row (see .mp-grid-summary-
  // sticky) can stick immediately below it at the right offset, instead of
  // either overlapping it or leaving a gap - measured via ResizeObserver
  // (not a hardcoded pixel value) since that height isn't constant, e.g. the
  // refresh row only appears once lastRefreshedAt/isManualRefreshing exist.
  useEffect(() => {
    const header = stickyHeaderRef.current;
    if (!header) return undefined;
    const update = () => setStickyHeaderHeight(header.getBoundingClientRect().height);
    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(header);
    return () => resizeObserver.disconnect();
  }, []);

  const handleScrollThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const container = gridScrollRef.current;
    if (!container) return;
    const track = e.currentTarget.parentElement;
    const trackRect = track.getBoundingClientRect();
    const startX = e.clientX;
    const startScrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    const handleMouseMove = (moveEvent) => {
      const deltaRatio = (moveEvent.clientX - startX) / trackRect.width;
      const deltaScroll = deltaRatio * container.scrollWidth;
      container.scrollLeft = Math.max(0, Math.min(maxScroll, startScrollLeft + deltaScroll));
    };
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleScrollTrackMouseDown = (e) => {
    const container = gridScrollRef.current;
    if (!container || e.target === gridScrollThumbRef.current) return;
    const trackRect = e.currentTarget.getBoundingClientRect();
    const clickRatio = (e.clientX - trackRect.left) / trackRect.width;
    const maxScroll = container.scrollWidth - container.clientWidth;
    container.scrollLeft = Math.max(0, Math.min(maxScroll, clickRatio * container.scrollWidth - container.clientWidth / 2));
  };

  const SCHEDULE_TYPE_OPTIONS = [
    { value: "FMBTHALI", label: "FMB" },
    { value: "MIQAAT", label: "Miqaat" },
  ];

  // A day can now hold multiple schedule entries (e.g. two separate menus
  // for the same day) - group all of them per day instead of picking one.
  const entriesByDay = useMemo(() => {
    const map = {};
    monthEntries.forEach((item) => {
      if (!item?.schedule_day) return;
      const key = toDateKey(new Date(item.schedule_day));
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => (a.id ?? 0) - (b.id ?? 0)));
    return map;
  }, [monthEntries]);

  // Every date always shows at least one row: every existing thaali_schedule
  // entry for that day, plus - now that the Caterer Grid's data lives here
  // too - a row for any day/type that has a real thaali_schedule_caterer
  // record but no thaali_schedule entry yet (a caterer set with no menu
  // picked out), plus (if there's neither) a single blank starter row, plus
  // any blank rows the user added via the "+" next to Date.
  const rowsForDay = (dateKey) => {
    const existing = (entriesByDay[dateKey] || []).map((entry) => ({ rowKey: `e${entry.id}`, entry }));
    const coveredTypes = new Set(existing.map(({ entry }) => entry.schedule_type));

    const catererOnly = (catererEntriesByDay[dateKey] || [])
      .filter((ce) => ce.caterer_id1 && !coveredTypes.has(ce.schedule_type))
      .map((ce) => ({ rowKey: `cat${ce.id}`, entry: null, catererOnlyEntry: ce }));

    const rows = [...existing, ...catererOnly];
    if (rows.length === 0) {
      rows.push({ rowKey: `new-${dateKey}`, entry: null });
    }
    (extraRows[dateKey] || []).forEach((rowKey) => rows.push({ rowKey, entry: null }));
    return rows;
  };

  const handleAddRow = (dateKey) => {
    nextLocalRowIdRef.current += 1;
    const rowKey = `new-${dateKey}-${nextLocalRowIdRef.current}`;
    setExtraRows((prev) => ({ ...prev, [dateKey]: [...(prev[dateKey] || []), rowKey] }));
  };

  const handleRemoveExtraRow = (dateKey, rowKey) => {
    setExtraRows((prev) => ({ ...prev, [dateKey]: (prev[dateKey] || []).filter((k) => k !== rowKey) }));
    setGridEdits((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  // Discards unsaved edits for a row, reverting it back to its last-saved
  // state (or blank, if it was never saved) - unlike handleRemoveExtraRow,
  // this doesn't delete the row itself, just the in-progress changes.
  const handleCancelRowEdits = (rowKey) => {
    setGridEdits((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  // defaultScheduleTypeValue only matters when entry is null - lets a
  // caterer-only row (see rowsForDay) default to the caterer record's own
  // type, since that's already-saved data, not a placeholder. A genuinely
  // blank new row (no default given at all) starts with no Type selected -
  // same blank-until-chosen treatment as Lead Caterer/dish fields - and
  // only defaults to FMB at save time (see handleSaveRow's `|| "FMBTHALI"`).
  const initialRowFor = (entry, defaultScheduleTypeValue) => {
    if (!entry) {
      const scheduleType = defaultScheduleTypeValue
        ? SCHEDULE_TYPE_OPTIONS.find((o) => o.value === defaultScheduleTypeValue) || null
        : null;
      return {
        id: null,
        dishSelections: {},
        scheduleType,
        notes: "",
        notes2: "",
        saveDraft: "1", // new rows default to Draft
      };
    }
    const niyaazType = NIYAAZ_TYPE_BY_SCHEDULE[entry.schedule_type] ?? 0;
    const dishSelections = {};
    parseMenu(entry.menu).forEach((dishName) => {
      const dish = dishByNameAndType[niyaazType]?.[dishName?.trim().toLowerCase()];
      if (dish?.dish_type) {
        if (!dishSelections[dish.dish_type]) dishSelections[dish.dish_type] = [];
        dishSelections[dish.dish_type].push({ value: dish.id, label: dish.dish_name });
      }
    });
    const scheduleType =
      SCHEDULE_TYPE_OPTIONS.find((o) => o.value === entry.schedule_type) || SCHEDULE_TYPE_OPTIONS[0];
    return {
      id: entry.id,
      dishSelections,
      scheduleType,
      notes: entry.notes || "",
      notes2: entry.notes2 || "",
      saveDraft: String(entry.save_draft) === "1" ? "1" : "0",
    };
  };

  // Resolves the effective Lead Caterer/Team Member 1/2/status/record-id for
  // a row: a real thaali_schedule_caterer record for this day+(effective)
  // type wins; failing that, a legacy caterer set directly on thaali_
  // schedule (cater_name1, no caterer-table record at all) is shown as a
  // plain, directly-editable "default" value - no status icon, no lock -
  // since it isn't actually a tracked/confirmed assignment, just whatever
  // happened to already be on the schedule row.
  function resolveCatererDefaults(entry, dateKey, scheduleTypeValue) {
    const catererEntry = getCatererEntryForRow(dateKey, scheduleTypeValue);
    if (catererEntry) {
      return {
        leadCaterer: catererEntry.leadCaterer,
        teamMember1: catererEntry.teamMember1,
        teamMember2: catererEntry.teamMember2,
        status: catererEntry.status,
        catererRecordId: catererEntry.id,
        isLegacyDefault: false,
      };
    }
    const legacyId = entry?.cater_name1 ? catererIdByName[entry.cater_name1.trim().toLowerCase()] : null;
    if (legacyId) {
      return {
        leadCaterer: catererOptionFor(legacyId),
        teamMember1: null,
        teamMember2: null,
        status: null,
        catererRecordId: null,
        isLegacyDefault: true,
      };
    }
    return {
      leadCaterer: null,
      teamMember1: null,
      teamMember2: null,
      status: null,
      catererRecordId: null,
      isLegacyDefault: false,
    };
  }

  const getEffectiveRow = (rowKey, entry, dateKey, catererOnlyEntry) => {
    const initial = initialRowFor(entry, catererOnlyEntry?.schedule_type);
    const edit = gridEdits[rowKey];
    const dishSelections = { ...initial.dishSelections, ...(edit?.dishSelections || {}) };
    // Every category always renders at least one (possibly empty) slot.
    categories.forEach((category) => {
      if (!dishSelections[category] || dishSelections[category].length === 0) {
        dishSelections[category] = [null];
      }
    });
    const scheduleType = edit && edit.scheduleType !== undefined ? edit.scheduleType : initial.scheduleType;
    const catererDefaults = resolveCatererDefaults(entry, dateKey, scheduleType?.value);
    const leadCatererEdited = Boolean(edit && edit.leadCaterer !== undefined);
    return {
      id: initial.id,
      dishSelections,
      scheduleType,
      leadCaterer: leadCatererEdited ? edit.leadCaterer : catererDefaults.leadCaterer,
      teamMember1: edit && edit.teamMember1 !== undefined ? edit.teamMember1 : catererDefaults.teamMember1,
      teamMember2: edit && edit.teamMember2 !== undefined ? edit.teamMember2 : catererDefaults.teamMember2,
      catererStatus: catererDefaults.status, // read-only display, not locally editable
      catererRecordId: catererDefaults.catererRecordId,
      // Still just the untouched legacy default, not something the user has
      // actually picked/confirmed - see handleSaveRow, which skips writing
      // to thaali_schedule_caterer for this case unless Team Members were
      // also added (a real signal the user means to track this row there).
      isLegacyDefaultCaterer: catererDefaults.isLegacyDefault && !leadCatererEdited,
      notes: edit && edit.notes !== undefined ? edit.notes : initial.notes,
      notes2: edit && edit.notes2 !== undefined ? edit.notes2 : initial.notes2,
      saveDraft: edit && edit.saveDraft !== undefined ? edit.saveDraft : initial.saveDraft,
    };
  };

  const handleDishSelect = (rowKey, category, slotIndex, option, currentSlots) => {
    const nextSlots = [...currentSlots];
    nextSlots[slotIndex] = option;
    setGridEdits((prev) => {
      const current = prev[rowKey] || {};
      return {
        ...prev,
        [rowKey]: {
          ...current,
          dishSelections: { ...(current.dishSelections || {}), [category]: nextSlots },
        },
      };
    });
  };

  const handleAddDishSlot = (rowKey, category, currentSlots) => {
    setGridEdits((prev) => {
      const current = prev[rowKey] || {};
      return {
        ...prev,
        [rowKey]: {
          ...current,
          dishSelections: { ...(current.dishSelections || {}), [category]: [...currentSlots, null] },
        },
      };
    });
  };

  const handleRemoveDishSlot = (rowKey, category, slotIndex, currentSlots) => {
    if (currentSlots.length <= 1) return;
    const nextSlots = currentSlots.filter((_, i) => i !== slotIndex);
    setGridEdits((prev) => {
      const current = prev[rowKey] || {};
      return {
        ...prev,
        [rowKey]: {
          ...current,
          dishSelections: { ...(current.dishSelections || {}), [category]: nextSlots },
        },
      };
    });
  };

  // Switching FMB <-> Miqaat changes which dish list is valid, so any
  // already-picked dishes (which may belong to the other niyaaz type) are
  // cleared and must be re-picked from the newly-filtered options. The
  // Caterer read-only display re-resolves itself from the new type via
  // resolveCatererDefaults, so it doesn't need clearing here.
  const handleScheduleTypeSelect = (rowKey, option) => {
    const blankDishSelections = {};
    categories.forEach((category) => {
      blankDishSelections[category] = [null];
    });
    setGridEdits((prev) => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        scheduleType: option,
        dishSelections: blankDishSelections,
      },
    }));
  };

  const handleSaveDraftToggle = (rowKey, value) => {
    setGridEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), saveDraft: value },
    }));
  };

  const handleNotesChange = (rowKey, value) => {
    setGridEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), notes: value },
    }));
  };

  const handleNotes2Change = (rowKey, value) => {
    setGridEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), notes2: value },
    }));
  };

  // Saving a row now branches on what's actually filled in:
  //  - Lead Caterer only (no dishes): create/update thaali_schedule_caterer
  //    only, status PENDING - no thaali_schedule draft is created.
  //  - Lead Caterer + dishes: thaali_schedule_caterer is saved as CONFIRMED
  //    (a full caterer+menu combo is considered a complete assignment) AND
  //    the thaali_schedule draft is created/updated as before - UNLESS the
  //    Lead Caterer is still the Default Caterer (e.g. a menu created here
  //    first, before anyone picked a real caterer from the Caterer Grid, see
  //    handleSaveRow's own caterer_id1 fallback below), which always stays
  //    PENDING instead - same "Default never auto-confirms/opens itself,
  //    only a manual pick does" rule as the Caterer Grid's own
  //    handleCatererLeadChange/handleCatererMarkOpen.
  //  - Dishes only, no caterer: unchanged - just the thaali_schedule draft.
  const handleSaveRow = (rowKey, dateKey, entry, catererOnlyEntry) => {
    const row = getEffectiveRow(rowKey, entry, dateKey, catererOnlyEntry);
    const dishArray = categories
      .flatMap((category) => row.dishSelections[category] || [])
      .filter(Boolean)
      .map((option) => ({ dish_id: option.value, quantity: "FULL", is_fateha: "false" }));

    const hasDishes = dishArray.length > 0;
    const hasCaterer = Boolean(row.leadCaterer);
    // A still-untouched legacy default (see resolveCatererDefaults/
    // getEffectiveRow) isn't a real pick the user made, so it shouldn't get
    // written into thaali_schedule_caterer on its own - unless Team Members
    // were also added, which is a real signal they mean to start tracking
    // this row there.
    const shouldSaveCatererRecord = hasCaterer && !(row.isLegacyDefaultCaterer && !row.teamMember1 && !row.teamMember2);

    if (!hasDishes && !hasCaterer) {
      toast.warning("Add at least one dish, or assign a Caterer from the Caterer Grid, before saving this row.");
      return;
    }

    const dayString = dateKey; // already YYYY-MM-DD
    setSavingRows((prev) => ({ ...prev, [rowKey]: true }));

    const requests = [];

    if (shouldSaveCatererRecord) {
      const catererRequestData = {
        schedule_day: dayString,
        schedule_type: row.scheduleType?.value || "FMBTHALI",
        caterer_id1: row.leadCaterer?.value,
        // ?? null - see the matching comment in handleSaveCatererRow below.
        caterer_id2: row.teamMember1?.value ?? null,
        caterer_id3: row.teamMember2?.value ?? null,
        status:
          String(row.leadCaterer?.value) === String(defaultCatererId)
            ? "PENDING"
            : hasDishes
            ? "CONFIRMED"
            : "PENDING",
        is_active: "1",
        source: "community",
        created_user: access_id,
      };
      requests.push(
        row.catererRecordId
          ? dispatch(updateCatererSchedule({ id: row.catererRecordId, values: catererRequestData }))
          : dispatch(createCatererSchedule(catererRequestData))
      );
    }

    if (hasDishes) {
      // Whether any dish picked for the Rice/Roti column is actually a
      // rice/roti item by name (isRice/isRoti) - drives allow_skip_rice/
      // allow_skip_roti on save, since the dish master's own attribute for
      // this isn't reliably set.
      const riceAllowSkip = (row.dishSelections.Rice || []).some((slot) => slot?.label && isRice(slot.label.toLowerCase()));
      const rotiAllowSkip = (row.dishSelections.Roti || []).some((slot) => slot?.label && isRoti(slot.label.toLowerCase()));

      const requestData = {
        schedule_type: row.scheduleType?.value || "FMBTHALI",
        schedule_day: dayString,
        pickup_start_time: dayString,
        pickup_end_time: dayString,
        rsvp_date: getRsvpDateForType(parseDateKeyLocal(dayString), row.scheduleType?.value),
        rsvp_time: "10.00 AM",
        is_niyaaz: "0",
        jamanType: "COOKING",
        dish: dishArray,
        // A brand-new schedule (no row.id yet) with no caterer already
        // resolved (from the Caterer Grid or a legacy thaali_schedule value)
        // defaults to the placeholder Default Caterer (id 1) rather than
        // being left blank.
        caterer_id1: row.leadCaterer?.value || (row.id ? undefined : DEFAULT_CATERER_ID),
        notes: row.notes || "",
        notes2: row.notes2 || "",
        dish_id: "",
        order_by: "",
        source: "community",
        created_user: access_id,
        is_active: "1",
        portion_type_code: "1",
        pickup_location_code: "2",
        quantity: "HALF",
        fateha_name: "",
        allow_skip_rice: riceAllowSkip ? "1" : "0",
        allow_skip_roti: rotiAllowSkip ? "1" : "0",
        allow_skip_mithas: "0",
        save_draft: row.saveDraft === "0" ? "0" : "1",
      };

      requests.push(
        row.id
          ? dispatch(Updatethali({ id: row.id, values: requestData })).then((res) => {
              if (res?.payload?.status === true) {
                toast.success(res?.payload?.message || (requestData.save_draft === "0" ? "Published" : "Draft saved"));
              } else {
                toast.error(res?.payload?.message || "Failed to save draft");
              }
            })
          : dispatch(createCalender(requestData)) // createCalender already toasts success/failure itself
      );
    }

    Promise.all(requests)
      .then(() => Promise.all([refetchSchedule(), refetchCatererSchedule()]))
      .then(() => {
        setGridEdits((prev) => {
          const next = { ...prev };
          delete next[rowKey];
          return next;
        });
        // Once a locally-added blank row is actually saved, it becomes a
        // real backend entry (picked up via entriesByDay on the next
        // render) - drop its placeholder so it doesn't also linger as an
        // extra empty row next to the one that now holds its data.
        setExtraRows((prev) => {
          if (!prev[dateKey]?.includes(rowKey)) return prev;
          return { ...prev, [dateKey]: prev[dateKey].filter((k) => k !== rowKey) };
        });
      })
      .finally(() => {
        setSavingRows((prev) => ({ ...prev, [rowKey]: false }));
      });
  };

  // ---- Caterer Grid (thaali_schedule_caterer) ----

  const catererMonthEntries = useMemo(
    () => filterEntriesByRange(catererScheduleList, rangeStart, rangeEnd),
    [catererScheduleList, rangeStart, rangeEnd]
  );

  const catererEntriesByDay = useMemo(() => {
    const map = {};
    catererMonthEntries.forEach((item) => {
      if (!item?.schedule_day) return;
      const key = toDateKey(new Date(item.schedule_day));
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => (a.id ?? 0) - (b.id ?? 0)));
    return map;
  }, [catererMonthEntries]);

  // Quick-glance coverage summary for the Caterer Grid - counts merged-in
  // days (a Daily Menu Grid caterer with no thaali_schedule_caterer record
  // yet) as Confirmed too, matching how they're actually displayed in the
  // grid below (see catererRowsForDay/initialCatererRowFromThaliEntry) -
  // that caterer is already live on the schedule as-is, unlike Open (which
  // is only the default for a caterer freshly picked from the Lead Caterer
  // dropdown - see handleCatererLeadChange).
  const catererGridSummary = useMemo(() => {
    // Sundays aren't an operating day, so they're excluded from Days
    // Shown/Caterer Missing here too - same rule the Daily Menu Grid's own
    // summary already follows (see dailyGridSummary below).
    const countedDays = visibleGridDays.filter((date) => date.getDay() !== 0);
    let daysWithCaterer = 0;
    let draftCount = 0;
    let pendingCount = 0;
    let declinedCount = 0;
    let confirmedCount = 0;
    let openCount = 0;
    countedDays.forEach((date) => {
      const dateKey = toDateKey(date);
      const realEntries = catererEntriesByDay[dateKey] || [];
      const thaliEntries = entriesByDay[dateKey] || [];
      const coveredTypes = new Set(realEntries.map((e) => e.schedule_type));
      const merged = thaliEntries.filter((e) => e.cater_name1 && !coveredTypes.has(e.schedule_type));
      // A merged row still on the Default Caterer (e.g. a menu created here
      // first, before anyone picked a real caterer from the Caterer Grid)
      // isn't a done assignment - same "Default stays Pending" rule as
      // initialCatererRowFromThaliEntry, which resolves this same row for
      // display.
      const mergedPendingCount = merged.filter((e) => {
        const catererId = catererIdByName[e.cater_name1?.trim().toLowerCase()];
        return String(catererId) === String(defaultCatererId);
      }).length;
      const mergedConfirmedCount = merged.length - mergedPendingCount;

      const realWithCaterer = realEntries.filter((e) => e.caterer_id1);
      if (realWithCaterer.length > 0 || merged.length > 0) daysWithCaterer += 1;
      draftCount += realWithCaterer.filter((e) => e.status === "DRAFT").length;
      pendingCount += realWithCaterer.filter((e) => (e.status || "PENDING") === "PENDING").length + mergedPendingCount;
      declinedCount += realWithCaterer.filter((e) => e.status === "DECLINED").length;
      confirmedCount += realWithCaterer.filter((e) => e.status === "CONFIRMED").length + mergedConfirmedCount;
      openCount += realWithCaterer.filter((e) => e.status === "OPEN").length;
    });
    return {
      totalDays: countedDays.length,
      daysWithCaterer,
      daysMissingCaterer: countedDays.length - daysWithCaterer,
      draftCount,
      pendingCount,
      declinedCount,
      confirmedCount,
      openCount,
    };
  }, [visibleGridDays, catererEntriesByDay, entriesByDay, catererIdByName, defaultCatererId]);

  // Keyed by "dateKey|scheduleType" -> the caterer-schedule entry, used by
  // the Daily Menu Grid to pull in the Lead Caterer/Team Members/status for
  // that specific day+type (see getCatererEntryForRow below).
  const catererEntryByDayAndType = useMemo(() => {
    const map = {};
    catererScheduleList.forEach((item) => {
      if (!item?.schedule_day || !item?.schedule_type) return;
      const key = `${toDateKey(new Date(item.schedule_day))}|${item.schedule_type}`;
      // If duplicates exist for the same day+type, prefer the one with a
      // lead caterer actually set.
      if (!map[key] || (!map[key].caterer_id1 && item.caterer_id1)) {
        map[key] = item;
      }
    });
    return map;
  }, [catererScheduleList]);

  // Full thaali_schedule_caterer record (if any) for a given day+type,
  // resolved into Select-option shape - drives the Daily Menu Grid's Lead
  // Caterer/Team Member 1/2 fields and status badge.
  function getCatererEntryForRow(dateKey, scheduleTypeValue) {
    const entry = catererEntryByDayAndType[`${dateKey}|${scheduleTypeValue || "FMBTHALI"}`];
    if (!entry) return null;
    return {
      id: entry.id,
      leadCaterer: catererOptionFor(entry.caterer_id1),
      teamMember1: teamMemberOptionFor(entry.caterer_id2),
      teamMember2: teamMemberOptionFor(entry.caterer_id3),
      status: entry.status || null,
    };
  }

  // Quick-glance coverage summary shown at the top of the Daily Menu Grid -
  // which visible days still need attention, at a glance, before scrolling
  // through the whole table. Sundays are excluded from every count here -
  // not an operating day, so they shouldn't skew "Days Shown"/"Menu
  // Missing"/etc.
  const dailyGridSummary = useMemo(() => {
    const countedDays = visibleGridDays.filter((date) => date.getDay() !== 0);
    let daysWithMenu = 0;
    let daysWithFmb = 0;
    let daysWithMiqaat = 0;
    countedDays.forEach((date) => {
      const dateKey = toDateKey(date);
      const entries = entriesByDay[dateKey] || [];
      if (entries.length > 0) {
        daysWithMenu += 1;
        if (entries.some((e) => e.schedule_type === "FMBTHALI")) daysWithFmb += 1;
        if (entries.some((e) => e.schedule_type === "MIQAAT")) daysWithMiqaat += 1;
      }
    });
    return {
      totalDays: countedDays.length,
      daysWithFmb,
      daysWithMiqaat,
      daysMissingMenu: countedDays.length - daysWithMenu,
    };
  }, [visibleGridDays, entriesByDay]);

  // Clicking a summary tile again clears the filter; clicking a different
  // one switches to it.
  const handleToggleCatererFilter = (value) => {
    setCatererStatusFilter((prev) => (prev === value ? null : value));
  };

  const handleToggleDailyGridFilter = (value) => {
    setDailyGridFilter((prev) => (prev === value ? null : value));
  };

  // Days that already have a caterer set on the Daily Menu Grid (thaali_
  // schedule.cater_name1) but no thaali_schedule_caterer record yet for
  // that same day+type get merged in here as a virtual "Confirmed" row -
  // a caterer already being on the live schedule is treated as a done
  // assignment, not something still Pending. Merging is scoped per
  // schedule_type so a day that already has a real Caterer Grid row for
  // FMB, say, doesn't also swallow up a separate not-yet-tracked Miqaat
  // assignment on the same day.
  const catererRowsForDay = (dateKey) => {
    const existing = (catererEntriesByDay[dateKey] || []).map((entry) => ({ rowKey: `ce${entry.id}`, entry }));
    const coveredTypes = new Set(existing.map(({ entry }) => entry.schedule_type));

    const mergedFromThali = (entriesByDay[dateKey] || [])
      .filter((thaliEntry) => thaliEntry.cater_name1 && !coveredTypes.has(thaliEntry.schedule_type))
      .map((thaliEntry) => ({ rowKey: `cthali-${thaliEntry.id}`, entry: null, thaliEntry }));

    const rows = [...existing, ...mergedFromThali];
    if (rows.length === 0) {
      rows.push({ rowKey: `cnew-${dateKey}`, entry: null });
    }
    (extraCatererRows[dateKey] || []).forEach((rowKey) => rows.push({ rowKey, entry: null }));
    return rows;
  };

  const handleAddCatererRow = (dateKey) => {
    nextLocalCatererRowIdRef.current += 1;
    const rowKey = `cnew-${dateKey}-${nextLocalCatererRowIdRef.current}`;
    setExtraCatererRows((prev) => ({ ...prev, [dateKey]: [...(prev[dateKey] || []), rowKey] }));
  };

  const handleRemoveExtraCatererRow = (dateKey, rowKey) => {
    setExtraCatererRows((prev) => ({ ...prev, [dateKey]: (prev[dateKey] || []).filter((k) => k !== rowKey) }));
    setCatererGridEdits((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setCatererTeamSlotsShown((prev) => {
      if (!(rowKey in prev)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const handleCancelCatererRowEdits = (rowKey) => {
    setCatererGridEdits((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setCatererTeamSlotsShown((prev) => {
      if (!(rowKey in prev)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  function catererOptionFor(id) {
    if (!id) return null;
    const caterer = catererMaster.find((c) => c.id === id);
    if (!caterer) return null;
    return { value: caterer.id, label: `${caterer?.first_name || ""} ${caterer?.last_name || ""}`.trim() };
  }

  // Same as catererOptionFor, but resolves against catererMasterMerged - a
  // Team Member's caterer_id might belong to someone admin/caterer-get-cook
  // filtered out (not flagged a cook), which would otherwise show up blank.
  function teamMemberOptionFor(id) {
    if (!id) return null;
    const caterer = catererMasterMerged.find((c) => c.id === id);
    if (!caterer) return null;
    return { value: caterer.id, label: `${caterer?.first_name || ""} ${caterer?.last_name || ""}`.trim() };
  }

  // An untouched blank row (any day, not just Sunday) shouldn't pre-show
  // "FMB" as if a type had actually been decided - it stays blank until the
  // user explicitly picks one (same "blank until chosen, defaults only at
  // save" treatment initialRowFor already gives the Daily Menu Grid's own
  // Type field).
  const initialCatererRowFor = (entry) => {
    if (!entry) {
      return {
        id: null,
        // A day with nothing set at all in the Caterer Grid yet shouldn't
        // silently default to FMB - leave Type blank until the user actually
        // picks one.
        scheduleType: null,
        leadCaterer: null,
        teamMember1: null,
        teamMember2: null,
        notes: "",
        status: null,
      };
    }
    const scheduleType =
      SCHEDULE_TYPE_OPTIONS.find((o) => o.value === entry.schedule_type) || SCHEDULE_TYPE_OPTIONS[0];
    return {
      id: entry.id,
      scheduleType,
      leadCaterer: catererOptionFor(entry.caterer_id1),
      teamMember1: teamMemberOptionFor(entry.caterer_id2),
      teamMember2: teamMemberOptionFor(entry.caterer_id3),
      notes: entry.notes || "",
      status: entry.status || null,
    };
  };

  // A day/type that already has a caterer set on the Daily Menu Grid but no
  // thaali_schedule_caterer record yet - built directly from the thaali_
  // schedule entry itself (cater_name1 is a name, not an id, so resolve it
  // the same way the Daily Menu Grid's own initialRowFor does). Shown as
  // already Confirmed since it's already live on the schedule as-is - NOT
  // the same as picking this same caterer fresh from the Lead Caterer
  // dropdown elsewhere (see handleCatererLeadChange), which defaults to
  // Open instead since that's a new pick, not something already reflected
  // on the schedule. Saving this row creates a real thaali_schedule_caterer
  // record (id stays null until then).
  const initialCatererRowFromThaliEntry = (thaliEntry) => {
    const catererId = catererIdByName[thaliEntry.cater_name1?.trim().toLowerCase()];
    const scheduleType =
      SCHEDULE_TYPE_OPTIONS.find((o) => o.value === thaliEntry.schedule_type) || SCHEDULE_TYPE_OPTIONS[0];
    return {
      id: null,
      scheduleType,
      leadCaterer: catererId ? catererOptionFor(catererId) : null,
      teamMember1: null,
      teamMember2: null,
      notes: "",
      // A menu created first on the Daily Menu Grid (before anyone picked a
      // real caterer here) falls back to the Default Caterer directly on
      // thaali_schedule - that's still an unassigned day, not a done
      // assignment, so it stays Pending same as everywhere else Default
      // resolves to Pending now. Any other caterer already on the live
      // schedule is still treated as Confirmed.
      status: String(catererId) === String(defaultCatererId) ? "PENDING" : "CONFIRMED",
    };
  };

  // The saved (pre-edit) row - used as-is by getEffectiveCatererRow below,
  // and on its own by the summary-tile filter (see catererStatusFilter) so
  // toggling a row's status/caterer while a filter is active doesn't yank
  // it out of view before the change is actually saved.
  const getSavedCatererRow = (entry, thaliEntry) =>
    entry
      ? initialCatererRowFor(entry)
      : thaliEntry
      ? initialCatererRowFromThaliEntry(thaliEntry)
      : initialCatererRowFor(null);

  const getEffectiveCatererRow = (rowKey, entry, thaliEntry) => {
    const initial = getSavedCatererRow(entry, thaliEntry);
    const edit = catererGridEdits[rowKey];
    if (!edit) return initial;
    return {
      id: initial.id,
      scheduleType: edit.scheduleType !== undefined ? edit.scheduleType : initial.scheduleType,
      leadCaterer: edit.leadCaterer !== undefined ? edit.leadCaterer : initial.leadCaterer,
      teamMember1: edit.teamMember1 !== undefined ? edit.teamMember1 : initial.teamMember1,
      teamMember2: edit.teamMember2 !== undefined ? edit.teamMember2 : initial.teamMember2,
      notes: edit.notes !== undefined ? edit.notes : initial.notes,
      // Status is now directly settable here (Pending/Confirmed toggle next
      // to Lead Caterer) instead of only ever coming from the mobile app -
      // see handleSaveCatererRow.
      status: edit.status !== undefined ? edit.status : initial.status,
    };
  };

  const handleCatererFieldChange = (rowKey, field, value) => {
    setCatererGridEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), [field]: value },
    }));
  };

  // Whatever the row's previous status was (e.g. Declined) was a verdict on
  // the PREVIOUS Lead Caterer, not this new one, so picking a caterer always
  // re-resolves status instead of carrying the old value forward - always to
  // Draft, even when the caterer picked is the Default Caterer, so a freshly
  // picked caterer starts out still-editable/not-yet-sent rather than
  // looking like an already-pending request. Open is no longer auto-derived
  // from picking Default; it's only ever set manually via the status toggle
  // below (see handleCatererMarkOpen).
  const handleCatererLeadChange = (rowKey, option) => {
    setCatererGridEdits((prev) => {
      const next = { ...(prev[rowKey] || {}), leadCaterer: option };
      if (option) next.status = "DRAFT";
      return { ...prev, [rowKey]: next };
    });
  };

  // Marking a row Open stages Lead Caterer back to Default Caterer + status
  // OPEN locally (picked up by the row's normal Save into thaali_schedule_
  // caterer, same as any other status/caterer edit) - and, if that day/type
  // already has a PUBLISHED menu on the Daily Menu Grid, also pushes the
  // Default Caterer into that already-live thali_schedule record right away.
  // There's no other save step that would ever reach thali_schedule from the
  // Caterer Grid, so unlike the local staging above, this part can't wait
  // for a manual Save click. Re-fetches the full record first (rather than
  // trusting the day's cached list entry) so the round-tripped update - the
  // same admin/thali/update/{id} endpoint used everywhere else, which
  // replaces the whole row - doesn't accidentally drop any field the list
  // response doesn't carry.
  //
  // scheduleTypeValue (not the row's own thaliEntry, from catererRowsForDay -
  // that's only ever populated for the "merged from thali, no caterer_
  // schedule record yet" case) is used to look the live thali_schedule entry
  // up fresh by day+type, the same way the Menu column does, so this also
  // fires for the far more common case of a row that already has its own
  // real thaali_schedule_caterer record.
  const handleCatererMarkOpen = (rowKey, dateKey, scheduleTypeValue) => {
    setCatererGridEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), leadCaterer: catererOptionFor(defaultCatererId), status: "OPEN" },
    }));

    const thaliEntry = (entriesByDay[dateKey] || []).find(
      (e) => e.schedule_type === (scheduleTypeValue || "FMBTHALI")
    );
    if (!thaliEntry?.id || String(thaliEntry.save_draft) === "1") return;

    dispatch(getThaliview(thaliEntry.id))
      .then((res) => {
        if (res?.payload?.status !== true) {
          toast.error(res?.payload?.message || "Failed to load the published menu before updating its caterer");
          return null;
        }
        const allthalilist = res.payload.thali;
        const alldishlist = res.payload.dish || [];
        const dishPayload = alldishlist.map((item) => ({
          dish_id: item?.dish_id ?? item?.dishid?.value ?? "",
          quantity: item?.quantity ?? item?.selecteddish ?? "",
          is_fateha: "false",
        }));
        const requestData = {
          schedule_type: allthalilist.schedule_type,
          save_draft: allthalilist.save_draft,
          is_niyaaz: allthalilist.is_niyaaz,
          jamanType: allthalilist.jamanType,
          dish: dishPayload,
          schedule_day: dateKey,
          pickup_start_time: dateKey,
          pickup_end_time: dateKey,
          caterer_id1: DEFAULT_CATERER_ID,
          caterer_id2: allthalilist.caterer_id2,
          notes: allthalilist.notes,
          order_by: allthalilist.order_by,
          source: "community",
          created_user: access_id,
          dish_id: allthalilist.dish_id,
          is_active: "1",
          portion_type_code: "1",
          pickup_location_code: "2",
          allow_skip_rice: allthalilist.allow_skip_rice,
          allow_skip_roti: allthalilist.allow_skip_roti,
          allow_skip_mithas: allthalilist.allow_skip_mithas,
          fateha_name: allthalilist.fateha_name,
          rsvp_date: allthalilist.rsvp_date,
          rsvp_time: allthalilist.rsvp_time,
        };
        return dispatch(Updatethali({ id: thaliEntry.id, values: requestData }));
      })
      .then((res) => {
        if (!res) return;
        if (res?.payload?.status === true) {
          toast.success("Caterer reset to Default on the published menu");
          refetchSchedule();
        } else {
          toast.error(res?.payload?.message || "Failed to update caterer on the published menu");
        }
      });
  };

  // How many Team Member slots the merged Caterer column shows for this row
  // (0-2) - defaults to however many are actually filled on the saved/edited
  // row (teamMember2 set -> 2, teamMember1 set -> 1, else 0), overridden
  // locally once the user clicks "Add Team Member" or the remove (x) button.
  const getCatererTeamSlotsShown = (rowKey, row) => {
    const override = catererTeamSlotsShown[rowKey];
    if (override !== undefined) return override;
    return row.teamMember2 ? 2 : row.teamMember1 ? 1 : 0;
  };

  const handleAddTeamMemberSlot = (rowKey, row) => {
    const current = getCatererTeamSlotsShown(rowKey, row);
    setCatererTeamSlotsShown((prev) => ({ ...prev, [rowKey]: Math.min(2, current + 1) }));
  };

  // Removing Team Member 1 while Team Member 2 is filled shifts Team Member
  // 2's value down into slot 1 instead of leaving a blank slot 1 in front of
  // a filled slot 2 - teamMember1/teamMember2 are fixed fields (not a real
  // array, capped by the thaali_schedule_caterer schema at exactly 2), so
  // "removing slot 1" has to mean "shift everything after it back" the same
  // way it would for a true array.
  const handleRemoveTeamMemberSlot = (rowKey, row, slotIndex) => {
    if (slotIndex === 0) {
      handleCatererFieldChange(rowKey, "teamMember1", row.teamMember2 || null);
      handleCatererFieldChange(rowKey, "teamMember2", null);
    } else {
      handleCatererFieldChange(rowKey, "teamMember2", null);
    }
    const current = getCatererTeamSlotsShown(rowKey, row);
    setCatererTeamSlotsShown((prev) => ({ ...prev, [rowKey]: Math.max(0, current - 1) }));
  };

  const handleSaveCatererRow = (rowKey, dateKey, entry, thaliEntry) => {
    const row = getEffectiveCatererRow(rowKey, entry, thaliEntry);
    if (!row.leadCaterer) {
      toast.warning("Pick a Lead Caterer before saving this row.");
      return;
    }

    const requestData = {
      schedule_day: dateKey,
      schedule_type: row.scheduleType?.value || "FMBTHALI",
      caterer_id1: row.leadCaterer?.value,
      // ?? null (not just ?.value, which is undefined for a removed team
      // member) - JSON.stringify drops undefined-valued keys entirely, so an
      // omitted caterer_id2/3 left the backend's previous value in place
      // instead of actually clearing it. Sending null explicitly clears it.
      caterer_id2: row.teamMember1?.value ?? null,
      caterer_id3: row.teamMember2?.value ?? null,
      notes: row.notes || "",
      // Pending/Confirmed toggle next to Lead Caterer - lets this screen
      // create a caterer assignment as already-Confirmed directly, instead
      // of only ever waiting on the mobile app to flip it.
      status: row.status || "PENDING",
      is_active: "1",
      source: "community",
      created_user: access_id,
    };

    setSavingCatererRows((prev) => ({ ...prev, [rowKey]: true }));

    const request = row.id
      ? dispatch(updateCatererSchedule({ id: row.id, values: requestData })).then((res) => {
          if (res?.payload?.status === true) {
            toast.success(res?.payload?.message || "Caterer schedule saved");
          } else {
            toast.error(res?.payload?.message || "Failed to save caterer schedule");
          }
        })
      : dispatch(createCatererSchedule(requestData));

    request
      .then(() => refetchCatererSchedule())
      .then(() => {
        setCatererGridEdits((prev) => {
          const next = { ...prev };
          delete next[rowKey];
          return next;
        });
        setExtraCatererRows((prev) => {
          if (!prev[dateKey]?.includes(rowKey)) return prev;
          return { ...prev, [dateKey]: prev[dateKey].filter((k) => k !== rowKey) };
        });
      })
      .finally(() => {
        setSavingCatererRows((prev) => ({ ...prev, [rowKey]: false }));
      });
  };

  // Deletes already-saved caterer-schedule request(s) for a day outright
  // (not just a status change) - takes an array since the Date cell's icon
  // acts on the whole day (usually just one row, occasionally FMB + Miqaat
  // both), same as the Daily Menu Grid's own Date-column delete icon. Only
  // rows with a real row.id are ever passed in - an unsaved row is just
  // discarded locally via handleRemoveExtraCatererRow. `date` is carried
  // along purely so the confirm modal can show what's about to be removed.
  const handleDeleteCatererRow = (catererRows, date) => {
    if (!catererRows?.length) return;
    setCatererRowPendingDelete({ rows: catererRows, date });
  };

  const closeCatererDeleteModal = () => {
    setCatererRowPendingDelete(null);
  };

  const confirmDeleteCatererRow = () => {
    if (!catererRowPendingDelete?.rows?.length) return;
    const catererRows = catererRowPendingDelete.rows;
    setCatererRowPendingDelete(null);
    const rowKeys = catererRows.map(({ rowKey }) => rowKey);
    setDeletingCatererRows((prev) => {
      const next = { ...prev };
      rowKeys.forEach((rowKey) => { next[rowKey] = true; });
      return next;
    });
    Promise.all(catererRows.map(({ row }) => dispatch(deleteCatererSchedule({ id: row.id }))))
      .then((resList) => {
        if (resList.every((res) => res?.payload?.status === true)) {
          toast.success("Caterer request deleted");
        } else {
          toast.error("Failed to delete one or more caterer requests");
        }
      })
      .then(() => refetchCatererSchedule())
      .then(() => {
        setCatererGridEdits((prev) => {
          const next = { ...prev };
          let changed = false;
          rowKeys.forEach((rowKey) => {
            if (next[rowKey]) { delete next[rowKey]; changed = true; }
          });
          return changed ? next : prev;
        });
        setCatererTeamSlotsShown((prev) => {
          const next = { ...prev };
          let changed = false;
          rowKeys.forEach((rowKey) => {
            if (rowKey in next) { delete next[rowKey]; changed = true; }
          });
          return changed ? next : prev;
        });
      })
      .finally(() => {
        setDeletingCatererRows((prev) => {
          const next = { ...prev };
          rowKeys.forEach((rowKey) => { delete next[rowKey]; });
          return next;
        });
      });
  };

  // Deletes the underlying Thaali schedule (menu) record(s) for a Daily Menu
  // Grid day - the same thali_schedule row(s) (and same deleteThaaliSchedule
  // endpoint) that Calendar Setup's per-entry Delete button removes. Takes
  // an array since a day can have both an FMB and a Miqaat entry saved -
  // the Date cell's icon acts on the whole day, so both get removed together.
  // `date` is carried along purely so the confirm modal can show what's
  // about to be removed.
  const handleDeleteThaliMenu = (menuEntries, date) => {
    if (!menuEntries?.length) return;
    setThaliMenuPendingDelete({ entries: menuEntries, date });
  };

  const closeThaliMenuDeleteModal = () => {
    setThaliMenuPendingDelete(null);
  };

  const confirmDeleteThaliMenu = () => {
    if (!thaliMenuPendingDelete?.entries?.length) return;
    const menuEntries = thaliMenuPendingDelete.entries;
    const ids = menuEntries.map((e) => e.id);
    setThaliMenuPendingDelete(null);
    setDeletingThaliMenuIds((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = true; });
      return next;
    });
    Promise.all(ids.map((id) => dispatch(deletethali(id))))
      .then((resList) => {
        if (resList.every((res) => res?.payload?.status === true)) {
          toast.success("Thali schedule deleted");
        } else {
          toast.error("Failed to delete one or more thali schedule entries");
        }
      })
      .then(() => refetchSchedule())
      .finally(() => {
        setDeletingThaliMenuIds((prev) => {
          const next = { ...prev };
          ids.forEach((id) => { delete next[id]; });
          return next;
        });
      });
  };

  // The four Statistics pages (FMB Menu/Caterer, Miqaat Menu/Caterer) all
  // share the same month/quarter range nav.
  const isStatsView =
    viewMode === "stats-fmb" ||
    viewMode === "stats-miqaat" ||
    viewMode === "stats-fmb-caterer" ||
    viewMode === "stats-miqaat-caterer";

  // Statistics view in quarterly mode steps by a full 3-month quarter per
  // click instead of one month; the Daily Menu Grid (and monthly stats)
  // always step by 1.
  const monthNavStep = isStatsView && statsRangeMode === "quarterly" ? 3 : 1;

  function shiftHijriMonth(year, month, delta) {
    const total = year * 12 + month + delta;
    return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
  }

  const handlePrevMonth = () => {
    const next = shiftHijriMonth(hijriYear, hijriMonth, -monthNavStep);
    setHijriYear(next.year);
    setHijriMonth(next.month);
  };

  const handleNextMonth = () => {
    const next = shiftHijriMonth(hijriYear, hijriMonth, monthNavStep);
    setHijriYear(next.year);
    setHijriMonth(next.month);
  };

  const statsQuarterEnd = shiftHijriMonth(hijriYear, hijriMonth, 2);

  // A summary-tile filter (Caterer Grid's catererStatusFilter, Daily Menu
  // Grid's dailyGridFilter) is scoped to whichever view set it - navigating
  // away via the top toggle row clears both, so switching views (or coming
  // back to the same one) always starts unfiltered again.
  const resetGridFilters = () => {
    setCatererStatusFilter(null);
    setDailyGridFilter(null);
  };

  // Coming from Statistics (which may be parked on a different month/quarter
  // via its own nav or quarterly mode), the Daily Menu Grid should always
  // open on the current Hijri month rather than wherever Statistics left off.
  const handleShowGrid = () => {
    if (isStatsView) {
      setHijriYear(initialHijri.year);
      setHijriMonth(initialHijri.month);
    }
    resetGridFilters();
    setViewMode("grid");
  };

  const handleShowCatererGrid = () => {
    if (isStatsView) {
      setHijriYear(initialHijri.year);
      setHijriMonth(initialHijri.month);
    }
    resetGridFilters();
    setViewMode("caterer");
  };

  const handleShowStats = (mode) => {
    resetGridFilters();
    setViewMode(mode);
  };

  return (
    <div>
      <Header />
      <div className="side_div_menu" style={{ display: "flex", height: "100%" }}>
        <Sidebarmenu />
        <section className="main_section">
          <div className="custam">
            <div className="main_section2">
              <div className="ExportInventory thali_section Thali_page">
                <div className="container-fluid mp-sticky-header" ref={stickyHeaderRef}>
                  <div className="thili_div">
                    <h1 className="title_heading">Menu Planning</h1>
                    <div className="mp-header-controls">
                      {isStatsView && (
                        <button
                          type="button"
                          className={`mp-stats-mode-switch ${statsRangeMode === "quarterly" ? "is-quarterly" : ""}`}
                          onClick={() => setStatsRangeMode((m) => (m === "monthly" ? "quarterly" : "monthly"))}
                          title="Toggle between monthly and quarterly statistics"
                        >
                          <span className="mp-stats-mode-option">Month</span>
                          <span className="mp-stats-mode-option">Quarter</span>
                          <span className="mp-stats-mode-thumb" />
                        </button>
                      )}
                      <div className="mp-month-nav">
                        <button className="btn thali_btn mp-month-btn" onClick={handlePrevMonth} title="Previous Hijri month">
                          &lsaquo;
                        </button>
                        <span className="mp-month-label">
                          <span className="mp-month-label-hijri">
                            {isStatsView && statsRangeMode === "quarterly"
                              ? `${HIJRI_MONTH_NAMES[hijriMonth]} ${hijriYear}H - ${HIJRI_MONTH_NAMES[statsQuarterEnd.month]} ${statsQuarterEnd.year}H`
                              : `${HIJRI_MONTH_NAMES[hijriMonth]} ${hijriYear}H`}
                          </span>
                          <span className="mp-month-label-gregorian">
                            {isStatsView && statsRangeMode === "quarterly"
                              ? `${moment(statsRangeStart).format("MMM D")} - ${moment(statsRangeEnd).format("MMM D, YYYY")}`
                              : `${moment(rangeStart).format("MMM D")} - ${moment(rangeEnd).format("MMM D, YYYY")}`}
                          </span>
                        </span>
                        <button className="btn thali_btn mp-month-btn" onClick={handleNextMonth} title="Next Hijri month">
                          &rsaquo;
                        </button>
                      </div>
                      {(viewMode === "grid" || viewMode === "caterer") && (
                        <div className="mp-refresh-row">
                          <button
                            type="button"
                            className={`mp-refresh-btn ${isManualRefreshing ? "is-refreshing" : ""}`}
                            onClick={() => refreshGridData(false)}
                            disabled={isManualRefreshing}
                            title="Refresh table data"
                          >
                            <Refresh fontSize="inherit" />
                            Refresh
                          </button>
                          <span className="mp-refresh-timestamp">
                            {lastRefreshedAt
                              ? `Last refreshed ${lastRefreshedAt.toLocaleTimeString()}`
                              : ""}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mp-view-toggle">
                    <div className="mp-view-toggle-group">
                      {SHOW_CATERER_GRID_TOGGLE && (
                        <button
                          className={`btn thali_btn mp-toggle-btn ${viewMode === "caterer" ? "active" : ""}`}
                          onClick={handleShowCatererGrid}
                        >
                          Caterer Grid
                        </button>
                      )}
                      <button
                        className={`btn thali_btn mp-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                        onClick={handleShowGrid}
                      >
                        Daily Menu Grid
                      </button>
                    </div>
                    <div className="mp-view-toggle-divider" />
                    <div className="mp-view-toggle-group">
                      <button
                        className={`btn thali_btn mp-toggle-btn ${viewMode === "stats-fmb" ? "active" : ""}`}
                        onClick={() => handleShowStats("stats-fmb")}
                      >
                        FMB Statistic
                      </button>
                      <button
                        className={`btn thali_btn mp-toggle-btn ${viewMode === "stats-fmb-caterer" ? "active" : ""}`}
                        onClick={() => handleShowStats("stats-fmb-caterer")}
                      >
                        FMB Caterer Statistics
                      </button>
                    </div>
                    <div className="mp-view-toggle-divider" />
                    <div className="mp-view-toggle-group">
                      <button
                        className={`btn thali_btn mp-toggle-btn mp-toggle-btn-miqaat ${viewMode === "stats-miqaat" ? "active" : ""}`}
                        onClick={() => handleShowStats("stats-miqaat")}
                      >
                        Miqaat Statistics
                      </button>
                      <button
                        className={`btn thali_btn mp-toggle-btn mp-toggle-btn-miqaat ${viewMode === "stats-miqaat-caterer" ? "active" : ""}`}
                        onClick={() => handleShowStats("stats-miqaat-caterer")}
                      >
                        Miqaat Caterer Statistics
                      </button>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="Loading-img">
                    <img src={loadergif} alt="Loading" />
                  </div>
                ) : viewMode === "grid" ? (
                  <div className="container-fluid mp-grid-view-container">
                    <div className="mp-grid-sticky-block" ref={gridStickyBlockRef} style={{ top: stickyHeaderHeight }}>
                      <div className="mp-grid-summary-row">
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-accent mp-grid-summary-tile-clickable ${
                            dailyGridFilter === null ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => setDailyGridFilter(null)}
                        >
                          <span className="mp-grid-summary-value">{dailyGridSummary.totalDays}</span>
                          <span className="mp-grid-summary-label">Days Shown</span>
                          <span className="mp-grid-summary-sublabel">{HIJRI_MONTH_NAMES[hijriMonth]} {hijriYear}H</span>
                        </div>
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-warn mp-grid-summary-tile-clickable ${
                            dailyGridFilter === "menuMissing" ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => handleToggleDailyGridFilter("menuMissing")}
                        >
                          <span className="mp-grid-summary-value">{dailyGridSummary.daysMissingMenu}</span>
                          <span className="mp-grid-summary-label">Menu Missing</span>
                        </div>
                        <div className="mp-grid-summary-divider" />
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-clickable ${
                            dailyGridFilter === "fmb" ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => handleToggleDailyGridFilter("fmb")}
                        >
                          <span className="mp-grid-summary-value">{dailyGridSummary.daysWithFmb}</span>
                          <span className="mp-grid-summary-label">FMB</span>
                        </div>
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-miqaat mp-grid-summary-tile-clickable ${
                            dailyGridFilter === "miqaat" ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => handleToggleDailyGridFilter("miqaat")}
                        >
                          <span className="mp-grid-summary-value">{dailyGridSummary.daysWithMiqaat}</span>
                          <span className="mp-grid-summary-label">Miqaat</span>
                        </div>
                      </div>
                      <div className="mp-grid-toolbar">
                        <p className="mp-empty-note mp-grid-hint">
                          Pick a dish per category (and a caterer), then hit the save icon for that row.
                          Use the + next to a dish list to add another dish of that type, or + next to Date to add a
                          whole second menu for that day. Saved rows show up as drafts on the Calendar Setup page for
                          final review and publish.
                        </p>
                        <div className="mp-grid-actions">
                          <div className="mp-layout-dropdown" ref={menuLayoutDropdownRef}>
                            <button
                              type="button"
                              className={`mp-layout-dropdown-btn ${menuLayoutMenuOpen ? "is-open" : ""}`}
                              onClick={() => setMenuLayoutMenuOpen((v) => !v)}
                              aria-haspopup="listbox"
                              aria-expanded={menuLayoutMenuOpen}
                            >
                              {menuLayout === "grid" ? <ViewList fontSize="small" /> : <ViewColumn fontSize="small" />}
                              <span>{menuLayout === "grid" ? "Grid View" : "Column View"}</span>
                              <ArrowDropDown fontSize="small" className="mp-layout-dropdown-caret" />
                            </button>
                            {menuLayoutMenuOpen && (
                              <ul className="mp-layout-dropdown-menu" role="listbox">
                                <li
                                  role="option"
                                  aria-selected={menuLayout === "column"}
                                  className={menuLayout === "column" ? "is-selected" : ""}
                                  onClick={() => handleMenuLayoutChange("column")}
                                >
                                  <ViewColumn fontSize="small" /> Column View
                                </li>
                                <li
                                  role="option"
                                  aria-selected={menuLayout === "grid"}
                                  className={menuLayout === "grid" ? "is-selected" : ""}
                                  onClick={() => handleMenuLayoutChange("grid")}
                                >
                                  <ViewList fontSize="small" /> Grid View
                                </li>
                              </ul>
                            )}
                          </div>
                          {isCurrentMonth && (
                            <button
                              type="button"
                              className="btn thali_btn mp-toggle-btn mp-collapse-past-btn"
                              onClick={() => setPastDaysCollapsed((v) => !v)}
                            >
                              {pastDaysCollapsed ? "Show Past Days" : "Hide Past Days"}
                            </button>
                          )}
                          <div className="mp-dish-search" title="Look up which dish type and niyaaz a dish belongs to">
                            <Select
                              classNamePrefix="mp-dish-search-select"
                              isClearable
                              placeholder="Search dish name..."
                              options={dishSearchOptions}
                              value={dishSearchValue}
                              onChange={setDishSearchValue}
                              formatOptionLabel={formatDishSearchOption}
                              noOptionsMessage={() => "No dish found"}
                              {...selectMenuPortalProps}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mp-grid-head-clone" ref={gridHeadCloneRef} aria-hidden="true" />
                    <div className="mp-grid-scroll" ref={gridScrollRef}>
                      <table className={`mp-grid-table ${menuLayout === "grid" ? "mp-grid-table-menu-layout" : ""}`}>
                        <thead>
                          <tr>
                            <th className="mp-grid-save-icon-col"></th>
                            <th className="mp-grid-day-col">Day</th>
                            <th className="mp-grid-date-col">Date</th>
                            <th className="mp-grid-type-col">Type</th>
                            {menuLayout === "grid" ? (
                              <th className="mp-grid-menu-col">Menu</th>
                            ) : (
                              categories.map((category) => <th key={category}>{category}</th>)
                            )}
                            <th>Notes</th>
                            <th>Temp Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleGridDays.map((date) => {
                            const dateKey = toDateKey(date);
                            const hijri = getHijriParts(date);
                            const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
                            const isToday = dateKey === todayKey;
                            const isPast = dateKey < todayKey;
                            const isSunday = date.getDay() === 0;
                            // Resolve each row once up front so the summary-tile filter (see
                            // dailyGridFilter) can reuse the resolved row below - a row
                            // currently being edited (dirty), or one just added via the Date
                            // cell's "+ Add" button (extraRows), always stays visible
                            // regardless of the filter, otherwise clicking Add while filtered
                            // would add a row and immediately hide it again.
                            const allRows = rowsForDay(dateKey).map((r) => ({
                              ...r,
                              row: getEffectiveRow(r.rowKey, r.entry, dateKey, r.catererOnlyEntry),
                            }));
                            const rows = !dailyGridFilter
                              ? allRows
                              : allRows.filter(({ rowKey, entry, row: r }) => {
                                  if (gridEdits[rowKey]) return true;
                                  if ((extraRows[dateKey] || []).includes(rowKey)) return true;
                                  if (dailyGridFilter === "menuMissing") return !entry;
                                  if (dailyGridFilter === "fmb") return r.scheduleType?.value === "FMBTHALI";
                                  if (dailyGridFilter === "miqaat") return r.scheduleType?.value === "MIQAAT";
                                  return true;
                                });
                            if (rows.length === 0) return null;
                            // Date cell is shared (rowSpan) across every row for the day, so
                            // whether it shows the Miqaat badge has to consider all of the
                            // day's VISIBLE rows, not just one - a day with a mix of FMB and
                            // Miqaat rows still counts as "has Miqaat" as long as that Miqaat
                            // row is still shown under the current filter.
                            const dayHasMiqaat = rows.some(({ row: r }) => r.scheduleType?.value === "MIQAAT");
                            // Real saved thali_schedule entries for the day (usually just one,
                            // occasionally FMB + Miqaat both) - what the Date cell's delete icon
                            // removes, via the same endpoint as Calendar Setup's own Delete.
                            const dayMenuEntries = rows.map(({ entry }) => entry).filter(Boolean);

                            return rows.map(({ rowKey, entry, catererOnlyEntry, row }, rowIndex) => {
                              const isDirty = Boolean(gridEdits[rowKey]);
                              const isSaved = Boolean(row.id) && !isDirty;
                              // save_draft "1" means still a draft - anything else (including
                              // "0" or missing/legacy entries) is treated as published.
                              const isRowPublished = Boolean(entry) && String(entry.save_draft) !== "1";
                              const isRowLocked = isPast || isRowPublished;
                              const isRemovableExtra = !entry && (extraRows[dateKey] || []).includes(rowKey);
                              const rowClass = [
                                isSunday ? "mp-grid-row-sunday" : "",
                                isDirty ? "mp-grid-row-dirty" : "",
                                isToday ? "mp-grid-row-today" : "",
                                isPast ? "mp-grid-row-past" : "",
                              ].filter(Boolean).join(" ");

                              return (
                                <tr key={rowKey} className={rowClass}>
                                  <td className="mp-grid-save-icon-col">
                                    <div className="mp-grid-save-icon-wrap">
                                      {!isPast && (
                                        <button
                                          type="button"
                                          className="mp-grid-save-icon-btn"
                                          title={isRowPublished ? "Published" : isSaved ? "Saved" : "Save Draft"}
                                          disabled={!isDirty || savingRows[rowKey] || isRowLocked}
                                          onClick={() => handleSaveRow(rowKey, dateKey, entry, catererOnlyEntry)}
                                        >
                                          <Save fontSize="small" />
                                          {isSaved && (
                                            <span title={isRowPublished ? "Published" : "Draft"}>
                                              <CheckCircle
                                                className={`mp-grid-save-tick ${
                                                  isRowPublished ? "mp-grid-save-tick-published" : "mp-grid-save-tick-draft"
                                                }`}
                                                fontSize="small"
                                              />
                                            </span>
                                          )}
                                        </button>
                                      )}
                                      {!isPast && isDirty && (
                                        <button
                                          type="button"
                                          className="mp-grid-cancel-icon-btn"
                                          title="Discard changes"
                                          onClick={() => handleCancelRowEdits(rowKey)}
                                        >
                                          <Undo fontSize="small" />
                                        </button>
                                      )}
                                      {!isPast && isRemovableExtra && (
                                        <button
                                          type="button"
                                          className="mp-grid-remove-row-btn"
                                          title="Remove this row"
                                          onClick={() => handleRemoveExtraRow(dateKey, rowKey)}
                                        >
                                          <Close fontSize="inherit" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  {rowIndex === 0 && (
                                    <td className="mp-grid-day-col" rowSpan={rows.length}>
                                      {weekday}
                                    </td>
                                  )}
                                  {rowIndex === 0 && (
                                    <td className="mp-grid-date-col" rowSpan={rows.length}>
                                      <div className="mp-grid-date-hijri">{hijri.day} {hijri.month}</div>
                                      <div className="mp-grid-date-greg">{formatDate(date)}</div>
                                      {isToday && <span className="mp-today-tag mp-date-tag-below">Today</span>}
                                      {dayHasMiqaat && <span className="mp-miqaat-tag mp-date-tag-below">Miqaat</span>}
                                      {!isPast && (
                                        <div className="mp-grid-date-actions-row">
                                          <button
                                            type="button"
                                            className="mp-grid-add-row-btn"
                                            title="Add another menu for this day"
                                            onClick={() => handleAddRow(dateKey)}
                                          >
                                            <AddCircleOutline fontSize="inherit" /> Add Menu
                                          </button>
                                          {dayMenuEntries.length > 0 && (
                                            <button
                                              type="button"
                                              className="mp-caterer-delete-btn mp-grid-date-delete-btn"
                                              title="Delete this day's Thali schedule"
                                              disabled={dayMenuEntries.some((e) => deletingThaliMenuIds[e.id])}
                                              onClick={() => handleDeleteThaliMenu(dayMenuEntries, date)}
                                            >
                                              <DeleteOutline fontSize="inherit" />
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  )}
                                  <td className="mp-grid-type-col">
                                    <Select
                                      classNamePrefix="mp-select"
                                      className="mp-grid-type-select"
                                      isDisabled={isRowLocked}
                                      placeholder="Type"
                                      options={SCHEDULE_TYPE_OPTIONS}
                                      value={row.scheduleType}
                                      onChange={(option) => handleScheduleTypeSelect(rowKey, option)}
                                      {...selectMenuPortalProps}
                                    />
                                    {/* Draft/Publish for this row's own thali_schedule save - same
                                        visual language as the Caterer Grid's Pending/Declined/
                                        Confirmed/Open toggle (Draft mirrors Open's blue tint,
                                        Publish mirrors Confirmed's green tint). Only affects what
                                        save_draft gets sent on the next Save click below - it
                                        doesn't save by itself. Hidden for a day with no schedule
                                        set at all yet (no entry, nothing entered locally either) -
                                        there's nothing to Draft/Publish until the user actually
                                        starts filling the row in. */}
                                    {(entry || isDirty) && (
                                      <div className="mp-grid-draft-toggle">
                                        <button
                                          type="button"
                                          className={`mp-grid-draft-toggle-btn mp-grid-draft-toggle-draft ${
                                            row.saveDraft !== "0" ? "is-active" : ""
                                          }`}
                                          disabled={isRowLocked}
                                          title="Save as Draft"
                                          onClick={() => handleSaveDraftToggle(rowKey, "1")}
                                        >
                                          <Pending fontSize="inherit" /> Draft
                                        </button>
                                        <button
                                          type="button"
                                          className={`mp-grid-draft-toggle-btn mp-grid-draft-toggle-publish ${
                                            row.saveDraft === "0" ? "is-active" : ""
                                          }`}
                                          disabled={isRowLocked}
                                          title="Publish"
                                          onClick={() => handleSaveDraftToggle(rowKey, "0")}
                                        >
                                          <CheckCircleOutline fontSize="inherit" /> Publish
                                        </button>
                                      </div>
                                    )}
                                    {/* Caterer is assigned from the Caterer Grid now - shown here
                                        read-only for reference, not editable. A day with no
                                        schedule set at all yet (no entry, no caterer-only merge)
                                        just hides this rather than showing "No Caterer". */}
                                    {(entry || catererOnlyEntry) && (
                                      <div className="mp-grid-type-caterer-readonly" title={row.leadCaterer?.label || ""}>
                                        <Person fontSize="inherit" className="mp-grid-type-caterer-icon" />
                                        <span>{row.leadCaterer?.label || "No Caterer"}</span>
                                      </div>
                                    )}
                                  </td>
                                  {(() => {
                                    // Same slot list + Add button either way - Column View puts each
                                    // dish type in its own <td>, Grid View stacks them as labelled
                                    // rows inside one Menu cell.
                                    const renderDishSlots = (category) => {
                                      const slots = row.dishSelections[category] || [null];
                                      const options = getDishOptions(category, row.scheduleType?.value, dateKey);
                                      return (
                                        <>
                                        {slots.map((slot, slotIndex) => (
                                          <div className="mp-grid-dish-slot" key={slotIndex} title={slot?.label || ""}>
                                            <Select
                                              classNamePrefix="mp-select"
                                              className="mp-grid-dish-slot-select"
                                              isClearable
                                              isDisabled={isRowLocked}
                                              placeholder="--"
                                              options={options}
                                              value={slot}
                                              onChange={(option) => handleDishSelect(rowKey, category, slotIndex, option, slots)}
                                              components={{ ValueContainer: DishTypeValueContainer }}
                                              dishTypeLabel={category}
                                              formatOptionLabel={formatOptionLabelWithCounts}
                                              {...dishSelectPortalProps}
                                            />
                                            {!isRowLocked && slots.length > 1 && (
                                              <button
                                                type="button"
                                                className="mp-grid-dish-remove-btn"
                                                title="Remove"
                                                onClick={() => handleRemoveDishSlot(rowKey, category, slotIndex, slots)}
                                              >
                                                <Close fontSize="inherit" />
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                        {!isRowLocked && (
                                          <button
                                            type="button"
                                            className="mp-grid-add-dish-btn"
                                            title={`Add another ${category} dish`}
                                            onClick={() => handleAddDishSlot(rowKey, category, slots)}
                                          >
                                            <AddCircleOutline fontSize="inherit" /> Add
                                          </button>
                                        )}
                                        </>
                                      );
                                    };
                                    if (menuLayout === "grid") {
                                      return (
                                        <td className="mp-grid-cell mp-grid-menu-col">
                                          {categories.map((category) => (
                                            <div key={category} className="mp-grid-menu-row">
                                              <div className="mp-grid-menu-row-label">{category}</div>
                                              <div className="mp-grid-menu-row-dishes">{renderDishSlots(category)}</div>
                                            </div>
                                          ))}
                                        </td>
                                      );
                                    }
                                    return categories.map((category) => (
                                      <td
                                        key={category}
                                        className={`mp-grid-cell mp-grid-dish-cell mp-grid-dish-cell-${category.toLowerCase()}`}
                                      >
                                        {renderDishSlots(category)}
                                      </td>
                                    ));
                                  })()}
                                  <td className="mp-grid-cell">
                                    <textarea
                                      className="mp-grid-notes-input"
                                      title={row.notes || ""}
                                      disabled={isRowLocked}
                                      placeholder="Notes..."
                                      value={row.notes || ""}
                                      onChange={(e) => handleNotesChange(rowKey, e.target.value)}
                                    />
                                  </td>
                                  <td className="mp-grid-cell">
                                    <textarea
                                      className="mp-grid-notes-input"
                                      title={row.notes2 || ""}
                                      disabled={isRowLocked}
                                      placeholder="Temp Notes..."
                                      value={row.notes2 || ""}
                                      onChange={(e) => handleNotes2Change(rowKey, e.target.value)}
                                    />
                                  </td>
                                </tr>
                              );
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mp-grid-custom-scrollbar" ref={gridScrollBarRef} onMouseDown={handleScrollTrackMouseDown}>
                      <div
                        className="mp-grid-custom-scrollbar-thumb"
                        ref={gridScrollThumbRef}
                        onMouseDown={handleScrollThumbMouseDown}
                      />
                    </div>
                  </div>
                ) : viewMode === "caterer" ? (
                  <div className="container-fluid mp-grid-view-container">
                    <div className="mp-grid-sticky-block" ref={gridStickyBlockRef} style={{ top: stickyHeaderHeight }}>
                      <div className="mp-grid-summary-row">
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-accent mp-grid-summary-tile-clickable ${
                            catererStatusFilter === null ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => setCatererStatusFilter(null)}
                        >
                          <span className="mp-grid-summary-value">{catererGridSummary.totalDays}</span>
                          <span className="mp-grid-summary-label">Days Shown</span>
                          <span className="mp-grid-summary-sublabel">{HIJRI_MONTH_NAMES[hijriMonth]} {hijriYear}H</span>
                        </div>
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-warn mp-grid-summary-tile-clickable ${
                            catererStatusFilter === "missing" ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => handleToggleCatererFilter("missing")}
                        >
                          <span className="mp-grid-summary-value">{catererGridSummary.daysMissingCaterer}</span>
                          <span className="mp-grid-summary-label">Caterer Missing</span>
                        </div>
                        <div className="mp-grid-summary-divider" />
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-clickable ${
                            catererStatusFilter === "assigned" ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => handleToggleCatererFilter("assigned")}
                        >
                          <span className="mp-grid-summary-value">{catererGridSummary.daysWithCaterer}</span>
                          <span className="mp-grid-summary-label">Caterer Assigned</span>
                        </div>
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-draft mp-grid-summary-tile-clickable ${
                            catererStatusFilter === "DRAFT" ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => handleToggleCatererFilter("DRAFT")}
                        >
                          <span className="mp-grid-summary-value">{catererGridSummary.draftCount}</span>
                          <span className="mp-grid-summary-label">
                            <EditNote fontSize="inherit" /> Draft
                          </span>
                        </div>
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-pending mp-grid-summary-tile-clickable ${
                            catererStatusFilter === "PENDING" ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => handleToggleCatererFilter("PENDING")}
                        >
                          <span className="mp-grid-summary-value">{catererGridSummary.pendingCount}</span>
                          <span className="mp-grid-summary-label">
                            <HourglassEmpty fontSize="inherit" /> Pending
                          </span>
                        </div>
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-declined mp-grid-summary-tile-clickable ${
                            catererStatusFilter === "DECLINED" ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => handleToggleCatererFilter("DECLINED")}
                        >
                          <span className="mp-grid-summary-value">{catererGridSummary.declinedCount}</span>
                          <span className="mp-grid-summary-label">
                            <Cancel fontSize="inherit" /> Declined
                          </span>
                        </div>
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-good mp-grid-summary-tile-clickable ${
                            catererStatusFilter === "CONFIRMED" ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => handleToggleCatererFilter("CONFIRMED")}
                        >
                          <span className="mp-grid-summary-value">{catererGridSummary.confirmedCount}</span>
                          <span className="mp-grid-summary-label">
                            <CheckCircleOutline fontSize="inherit" /> Confirmed
                          </span>
                        </div>
                        <div
                          className={`mp-grid-summary-tile mp-grid-summary-tile-open mp-grid-summary-tile-clickable ${
                            catererStatusFilter === "OPEN" ? "mp-grid-summary-tile-active" : ""
                          }`}
                          onClick={() => handleToggleCatererFilter("OPEN")}
                        >
                          <span className="mp-grid-summary-value">{catererGridSummary.openCount}</span>
                          <span className="mp-grid-summary-label">
                            <Pending fontSize="inherit" /> Open
                          </span>
                        </div>
                      </div>
                      <p className="mp-empty-note mp-grid-hint">
                        Assign the Lead Caterer (and up to 2 team members) per day. Once a Lead Caterer is set here,
                        the Daily Menu Grid's Caterer column for that day/type is prefilled with it, and locked once
                        Confirmed. A freshly picked Lead Caterer starts as Draft. Use the Draft/Pending/Declined/
                        Confirmed toggle next to Lead Caterer to set status directly - it can also still be updated
                        by the caterer from the mobile app.
                      </p>
                    </div>
                    <div className="mp-grid-head-clone" ref={gridHeadCloneRef} aria-hidden="true" />
                    <div className="mp-grid-scroll" ref={gridScrollRef}>
                      <table className="mp-grid-table mp-caterer-grid-table">
                        <thead>
                          <tr>
                            <th className="mp-grid-save-icon-col"></th>
                            <th className="mp-grid-day-col">Day</th>
                            <th className="mp-grid-date-col">Date</th>
                            <th className="mp-grid-type-col">Type</th>
                            <th>Caterer</th>
                            <th>Notes</th>
                            <th>Menu</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleGridDays.map((date) => {
                            const dateKey = toDateKey(date);
                            const hijri = getHijriParts(date);
                            const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
                            const isToday = dateKey === todayKey;
                            const isPast = dateKey < todayKey;
                            const isSunday = date.getDay() === 0;
                            // Resolve each row once up front so the summary-tile filter
                            // (see catererStatusFilter) can reuse that resolved row below
                            // instead of recomputing it - filtering itself uses the SAVED
                            // row (savedRow), not the edited one, so toggling a row's
                            // status/caterer while a filter is active doesn't make it
                            // disappear before the change is actually saved. A row currently
                            // being edited (dirty), or one just added via the Date cell's
                            // "+ Add" button (extraCatererRows), always stays visible
                            // regardless of the filter too - otherwise clicking Add while
                            // filtered would add a row and immediately hide it again. An
                            // untouched DEFAULT blank row (a day with nothing saved at all)
                            // still filters normally, so empty days don't linger under a
                            // filter that shouldn't match them.
                            const allRows = catererRowsForDay(dateKey).map((r) => ({
                              ...r,
                              row: getEffectiveCatererRow(r.rowKey, r.entry, r.thaliEntry),
                              savedRow: getSavedCatererRow(r.entry, r.thaliEntry),
                            }));
                            const rows = !catererStatusFilter
                              ? allRows
                              : allRows.filter(({ rowKey, savedRow }) => {
                                  if (catererGridEdits[rowKey]) return true;
                                  if ((extraCatererRows[dateKey] || []).includes(rowKey)) return true;
                                  if (catererStatusFilter === "missing") return !savedRow.leadCaterer;
                                  if (catererStatusFilter === "assigned") return Boolean(savedRow.leadCaterer);
                                  return (
                                    Boolean(savedRow.leadCaterer) && (savedRow.status || "PENDING") === catererStatusFilter
                                  );
                                });
                            if (rows.length === 0) return null;
                            // Saved caterer-schedule requests for the day (usually just one,
                            // occasionally FMB + Miqaat both) - what the Date cell's delete icon
                            // removes. Kept at the day level (not per-row, unlike before) to sit
                            // next to Add on the shared Date cell, consistent with the Daily Menu
                            // Grid's own Date-column delete icon.
                            const dayCatererRows = rows.filter(({ row: r }) => r?.id);

                            return rows.map(({ rowKey, entry, thaliEntry, row }, rowIndex) => {
                              const isDirty = Boolean(catererGridEdits[rowKey]);
                              const isSaved = Boolean(row.id) && !isDirty;
                              const isMiqaat = row.scheduleType?.value === "MIQAAT";
                              // Confirmed locks Type + Lead Caterer (same "settled, don't
                              // accidentally change it" idea as the Daily Menu Grid's own
                              // isRowLocked) - but NOT the status toggle itself, so the user
                              // can always flip status away from Confirmed, which
                              // immediately re-enables these fields again.
                              const isCatererLocked = isPast || row.status === "CONFIRMED";
                              // Team Members stay editable even once the row is Confirmed -
                              // only a past date locks them - since adding/swapping helpers
                              // shouldn't require un-confirming the Lead Caterer first.
                              const isTeamMemberLocked = isPast;
                              const isRemovableExtra = !entry && (extraCatererRows[dateKey] || []).includes(rowKey);
                              const rowClass = [
                                isSunday ? "mp-grid-row-sunday" : "",
                                isDirty ? "mp-grid-row-dirty" : "",
                                isToday ? "mp-grid-row-today" : "",
                                isPast ? "mp-grid-row-past" : "",
                              ].filter(Boolean).join(" ");

                              return (
                                <tr key={rowKey} className={rowClass}>
                                  <td className="mp-grid-save-icon-col">
                                    <div className="mp-grid-save-icon-wrap">
                                      {!isPast && (
                                        <button
                                          type="button"
                                          className="mp-grid-save-icon-btn"
                                          title={isSaved ? "Saved" : "Save Draft"}
                                          disabled={!isDirty || savingCatererRows[rowKey]}
                                          onClick={() => handleSaveCatererRow(rowKey, dateKey, entry, thaliEntry)}
                                        >
                                          <Save fontSize="small" />
                                          {isSaved && (
                                            <CheckCircle
                                              className={`mp-grid-save-tick mp-grid-save-tick-${(row.status || "PENDING").toLowerCase()}`}
                                              fontSize="small"
                                            />
                                          )}
                                        </button>
                                      )}
                                      {!isPast && isDirty && (
                                        <button
                                          type="button"
                                          className="mp-grid-cancel-icon-btn"
                                          title="Discard changes"
                                          onClick={() => handleCancelCatererRowEdits(rowKey)}
                                        >
                                          <Undo fontSize="small" />
                                        </button>
                                      )}
                                      {!isPast && isRemovableExtra && (
                                        <button
                                          type="button"
                                          className="mp-grid-remove-row-btn"
                                          title="Remove this row"
                                          onClick={() => handleRemoveExtraCatererRow(dateKey, rowKey)}
                                        >
                                          <Close fontSize="inherit" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  {rowIndex === 0 && (
                                    <td className="mp-grid-day-col" rowSpan={rows.length}>
                                      {weekday}
                                    </td>
                                  )}
                                  {rowIndex === 0 && (
                                    <td className="mp-grid-date-col" rowSpan={rows.length}>
                                      <div className="mp-grid-date-hijri">{hijri.day} {hijri.month}</div>
                                      <div className="mp-grid-date-greg">
                                        {formatDate(date)}
                                        {isToday && <span className="mp-today-tag">Today</span>}
                                      </div>
                                      {!isPast && (
                                        <div className="mp-grid-date-actions-row">
                                          <button
                                            type="button"
                                            className="mp-grid-add-row-btn"
                                            title="Add another caterer assignment for this day"
                                            onClick={() => handleAddCatererRow(dateKey)}
                                          >
                                            <AddCircleOutline fontSize="inherit" /> Add
                                          </button>
                                          {dayCatererRows.length > 0 && (
                                            <button
                                              type="button"
                                              className="mp-caterer-delete-btn mp-grid-date-delete-btn"
                                              title="Delete this day's caterer request"
                                              disabled={dayCatererRows.some(({ rowKey: rk }) => deletingCatererRows[rk])}
                                              onClick={() => handleDeleteCatererRow(dayCatererRows, date)}
                                            >
                                              <DeleteOutline fontSize="inherit" />
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  )}
                                  <td className="mp-grid-type-col">
                                    <Select
                                      classNamePrefix="mp-select"
                                      isDisabled={isCatererLocked}
                                      placeholder="Type"
                                      options={SCHEDULE_TYPE_OPTIONS}
                                      value={row.scheduleType}
                                      onChange={(option) => handleCatererFieldChange(rowKey, "scheduleType", option)}
                                      {...selectMenuPortalProps}
                                    />
                                    {isMiqaat && <span className="mp-miqaat-tag">Miqaat</span>}
                                  </td>
                                  <td className="mp-grid-cell mp-grid-caterer-cell mp-grid-caterer-merged-cell">
                                    <div className="mp-grid-caterer-slot mp-grid-caterer-slot-lead">
                                      <Select
                                        classNamePrefix="mp-select"
                                        className="mp-grid-caterer-select"
                                        isClearable
                                        isDisabled={isCatererLocked}
                                        placeholder="Lead Caterer"
                                        options={getCatererOptions(dateKey, defaultCatererId)}
                                        value={row.leadCaterer}
                                        onChange={(option) => handleCatererLeadChange(rowKey, option)}
                                        formatOptionLabel={formatOptionLabelWithCounts}
                                        {...catererSelectPortalProps}
                                      />
                                      {row.leadCaterer && (
                                        <div className="mp-caterer-status-toggle">
                                          <button
                                            type="button"
                                            className={`mp-caterer-status-toggle-btn mp-caterer-status-toggle-draft ${
                                              row.status === "DRAFT" ? "is-active" : ""
                                            }`}
                                            disabled={isPast}
                                            title="Mark as Draft"
                                            onClick={() => handleCatererFieldChange(rowKey, "status", "DRAFT")}
                                          >
                                            <EditNote fontSize="inherit" /> Draft
                                          </button>
                                          <button
                                            type="button"
                                            className={`mp-caterer-status-toggle-btn mp-caterer-status-toggle-pending ${
                                              (row.status || "PENDING") === "PENDING" ? "is-active" : ""
                                            }`}
                                            disabled={isPast}
                                            title="Mark as Pending"
                                            onClick={() => handleCatererFieldChange(rowKey, "status", "PENDING")}
                                          >
                                            <HourglassEmpty fontSize="inherit" /> Pending
                                          </button>
                                          <button
                                            type="button"
                                            className={`mp-caterer-status-toggle-btn mp-caterer-status-toggle-declined ${
                                              (row.status || "PENDING") === "DECLINED" ? "is-active" : ""
                                            }`}
                                            disabled={isPast}
                                            title="Mark as Declined"
                                            onClick={() => handleCatererFieldChange(rowKey, "status", "DECLINED")}
                                          >
                                            <Cancel fontSize="inherit" /> Declined
                                          </button>
                                          <button
                                            type="button"
                                            className={`mp-caterer-status-toggle-btn mp-caterer-status-toggle-confirmed ${
                                              (row.status || "PENDING") === "CONFIRMED" ? "is-active" : ""
                                            }`}
                                            disabled={isPast}
                                            title="Mark as Confirmed"
                                            onClick={() => handleCatererFieldChange(rowKey, "status", "CONFIRMED")}
                                          >
                                            <CheckCircleOutline fontSize="inherit" /> Confirmed
                                          </button>
                                          <button
                                            type="button"
                                            className={`mp-caterer-status-toggle-btn mp-caterer-status-toggle-open ${
                                              (row.status || "PENDING") === "OPEN" ? "is-active" : ""
                                            }`}
                                            disabled={isPast}
                                            title="Mark as Open"
                                            onClick={() => handleCatererMarkOpen(rowKey, dateKey, row.scheduleType?.value)}
                                          >
                                            <Pending fontSize="inherit" /> Open
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    {(() => {
                                      const teamSlotsShown = getCatererTeamSlotsShown(rowKey, row);
                                      return (
                                        <>
                                          {teamSlotsShown >= 1 && (
                                            <div className="mp-grid-caterer-slot">
                                              <Select
                                                classNamePrefix="mp-select"
                                                className="mp-grid-caterer-select mp-grid-team-member-select"
                                                isClearable
                                                isDisabled={isTeamMemberLocked}
                                                placeholder="Team Member 1"
                                                options={teamMemberCatererOptions}
                                                value={row.teamMember1}
                                                onChange={(option) => handleCatererFieldChange(rowKey, "teamMember1", option)}
                                                {...catererSelectPortalProps}
                                              />
                                              {!isTeamMemberLocked && (
                                                <button
                                                  type="button"
                                                  className="mp-grid-caterer-remove-btn"
                                                  title="Remove team member"
                                                  onClick={() => handleRemoveTeamMemberSlot(rowKey, row, 0)}
                                                >
                                                  <Close fontSize="inherit" />
                                                </button>
                                              )}
                                            </div>
                                          )}
                                          {teamSlotsShown >= 2 && (
                                            <div className="mp-grid-caterer-slot">
                                              <Select
                                                classNamePrefix="mp-select"
                                                className="mp-grid-caterer-select mp-grid-team-member-select"
                                                isClearable
                                                isDisabled={isTeamMemberLocked}
                                                placeholder="Team Member 2"
                                                options={teamMemberCatererOptions}
                                                value={row.teamMember2}
                                                onChange={(option) => handleCatererFieldChange(rowKey, "teamMember2", option)}
                                                {...catererSelectPortalProps}
                                              />
                                              {!isTeamMemberLocked && (
                                                <button
                                                  type="button"
                                                  className="mp-grid-caterer-remove-btn"
                                                  title="Remove team member"
                                                  onClick={() => handleRemoveTeamMemberSlot(rowKey, row, 1)}
                                                >
                                                  <Close fontSize="inherit" />
                                                </button>
                                              )}
                                            </div>
                                          )}
                                          {!isTeamMemberLocked && row.leadCaterer && teamSlotsShown < 2 && (
                                            <button
                                              type="button"
                                              className="mp-grid-add-dish-btn mp-grid-add-team-member-btn"
                                              title="Add a team member"
                                              onClick={() => handleAddTeamMemberSlot(rowKey, row)}
                                            >
                                              <AddCircleOutline fontSize="inherit" /> Add Team Member
                                            </button>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </td>
                                  <td className="mp-grid-cell">
                                    <textarea
                                      className="mp-grid-notes-input"
                                      title={row.notes || ""}
                                      disabled={isPast}
                                      placeholder="Notes..."
                                      value={row.notes || ""}
                                      onChange={(e) => handleCatererFieldChange(rowKey, "notes", e.target.value)}
                                    />
                                  </td>
                                  <td className="mp-grid-cell mp-grid-fmb-menu-col">
                                    {(() => {
                                      // A day can have both an FMB and a Miqaat row (each its own
                                      // thaali_schedule entry/menu) - match this row's own type
                                      // rather than always FMB, so a Miqaat caterer row shows the
                                      // Miqaat menu instead of "Menu not set".
                                      const menuEntry = (entriesByDay[dateKey] || []).find(
                                        (e) => e.schedule_type === (row.scheduleType?.value || "FMBTHALI")
                                      );
                                      const dishNames = menuEntry ? parseMenu(menuEntry.menu).filter(Boolean) : [];
                                      const isDraft = menuEntry ? String(menuEntry.save_draft) === "1" : false;
                                      if (!menuEntry || dishNames.length === 0) {
                                        return <span className="mp-grid-fmb-menu-empty">Menu not set</span>;
                                      }
                                      return (
                                        <div className="mp-grid-fmb-menu-readonly">
                                          {isDraft && <span className="mp-grid-fmb-menu-draft-tag">Draft</span>}
                                          <ul className="mp-grid-fmb-menu-list">
                                            {dishNames.map((name, i) => (
                                              <li key={i}>{name}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      );
                                    })()}
                                  </td>
                                </tr>
                              );
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mp-grid-custom-scrollbar" ref={gridScrollBarRef} onMouseDown={handleScrollTrackMouseDown}>
                      <div
                        className="mp-grid-custom-scrollbar-thumb"
                        ref={gridScrollThumbRef}
                        onMouseDown={handleScrollThumbMouseDown}
                      />
                    </div>
                  </div>
                ) : viewMode === "stats-fmb" ? (
                  <div className="container-fluid">
                    <div className="row mt-2 mb-4">
                      <div className="col-lg-4 col-md-6">
                        <div className="Week_list">
                          <div className="week_text">
                            <h4>Gregorian Range</h4>
                            <h1 className="mp-stat-range">
                              {formatDate(statsRangeStart)} - {formatDate(statsRangeEnd)}
                            </h1>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="Week_list INGREDIENTS_Cout">
                          <div className="week_text">
                            <h4>Scheduled Days</h4>
                            <h1>{summary.uniqueDays}</h1>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="Week_list">
                          <div className="week_text">
                            <h4>Thaali Entries</h4>
                            <h1>{summary.thaaliEntries}</h1>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h1 className="title_heading mp-section-heading">FMB Menu Statistic</h1>
                    {fmbItemFrequency.length === 0 ? (
                      <p className="mp-empty-note">No FMB dishes configured.</p>
                    ) : (
                      <div className="mp-category-scroll-row">
                        {fmbItemFrequency.map(({ category, dishes }) => (
                          <div
                            className={`mp-category-card ${dragOverCategory === category ? "mp-category-card-drop-target" : ""}`}
                            key={category}
                            onDragOver={(e) => handleCategoryDragOver(e, category)}
                            onDragLeave={() => handleCategoryDragLeave(category)}
                            onDrop={(e) => handleDropDishOnCategory(e, category, 0)}
                          >
                            <h5 className="thali-heding mp-category-title">
                              {category} ({dishes.filter((d) => d.count > 0).length}/{dishes.length})
                            </h5>
                            <ul className="mp-dish-list">
                              {dishes.map((dish) => (
                                <li
                                  key={dish.name}
                                  className={`mp-dish-row ${dish.count === 0 ? "mp-zero-count" : ""} ${
                                    draggedDish?.name === dish.name && draggedDish?.category === category
                                      ? "mp-dish-row-dragging"
                                      : ""
                                  }`}
                                  draggable={movingDishName === null}
                                  onDragStart={(e) => handleDishDragStart(e, dish.name, category, 0)}
                                  onDragEnd={handleDishDragEnd}
                                  title="Drag to another category to move this dish"
                                >
                                  <span
                                    className="mp-dish-name mp-dish-name-link"
                                    onClick={() => handleNavigateToDish(dish.name, "FMBTHALI")}
                                  >
                                    {dish.name}
                                  </span>
                                  <span className="mp-count-badge">
                                    {movingDishName === dish.name ? "…" : dish.count}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : viewMode === "stats-miqaat" ? (
                  <div className="container-fluid">
                    <div className="row mt-2 mb-4">
                      <div className="col-lg-4 col-md-6">
                        <div className="Week_list">
                          <div className="week_text">
                            <h4>Gregorian Range</h4>
                            <h1 className="mp-stat-range">
                              {formatDate(statsRangeStart)} - {formatDate(statsRangeEnd)}
                            </h1>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="Week_list INGREDIENTS_Cout">
                          <div className="week_text">
                            <h4>Scheduled Days</h4>
                            <h1>{summary.uniqueDays}</h1>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="Week_list Thali_Count">
                          <div className="week_text">
                            <h4>Miqaat / Niyaz Entries</h4>
                            <h1>{summary.miqaatEntries}</h1>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h1 className="title_heading mp-section-heading">Miqaat Menu Statistics</h1>
                    {miqaatItemFrequency.length === 0 ? (
                      <p className="mp-empty-note">No Miqaat dishes configured.</p>
                    ) : (
                      <div className="mp-category-scroll-row">
                        {miqaatItemFrequency.map(({ category, dishes }) => (
                          <div
                            className={`mp-category-card ${dragOverCategory === category ? "mp-category-card-drop-target" : ""}`}
                            key={category}
                            onDragOver={(e) => handleCategoryDragOver(e, category)}
                            onDragLeave={() => handleCategoryDragLeave(category)}
                            onDrop={(e) => handleDropDishOnCategory(e, category, 1)}
                          >
                            <h5 className="thali-heding mp-category-title">
                              {category} ({dishes.filter((d) => d.count > 0).length}/{dishes.length})
                            </h5>
                            <ul className="mp-dish-list">
                              {dishes.map((dish) => (
                                <li
                                  key={dish.name}
                                  className={`mp-dish-row ${dish.count === 0 ? "mp-zero-count" : ""} ${
                                    draggedDish?.name === dish.name && draggedDish?.category === category
                                      ? "mp-dish-row-dragging"
                                      : ""
                                  }`}
                                  draggable={movingDishName === null}
                                  onDragStart={(e) => handleDishDragStart(e, dish.name, category, 1)}
                                  onDragEnd={handleDishDragEnd}
                                  title="Drag to another category to move this dish"
                                >
                                  <span
                                    className="mp-dish-name mp-dish-name-link"
                                    onClick={() => handleNavigateToDish(dish.name, "MIQAAT")}
                                  >
                                    {dish.name}
                                  </span>
                                  <span className="mp-count-badge">
                                    {movingDishName === dish.name ? "…" : dish.count}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : viewMode === "stats-fmb-caterer" ? (
                  <div className="container-fluid">
                    <div className="row mt-2 mb-4">
                      <div className="col-lg-4 col-md-6">
                        <div className="Week_list">
                          <div className="week_text">
                            <h4>Gregorian Range</h4>
                            <h1 className="mp-stat-range">
                              {formatDate(statsRangeStart)} - {formatDate(statsRangeEnd)}
                            </h1>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="Week_list INGREDIENTS_Cout">
                          <div className="week_text">
                            <h4>Scheduled Days</h4>
                            <h1>{summary.uniqueDays}</h1>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h1 className="title_heading mp-section-heading">FMB Caterer Statistics</h1>
                    {fmbCatererFrequency.length === 0 ? (
                      <p className="mp-empty-note">No caterer data available.</p>
                    ) : (
                      <div className="mp-category-card mp-caterer-card">
                        <ul className="mp-dish-list">
                          {fmbCatererFrequency.map((caterer) => (
                            <li
                              key={caterer.name}
                              className={`mp-dish-row ${caterer.count === 0 ? "mp-zero-count" : ""}`}
                            >
                              <span className="mp-dish-name">{caterer.name}</span>
                              <span className="mp-count-badge">{caterer.count}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : viewMode === "stats-miqaat-caterer" ? (
                  <div className="container-fluid">
                    <div className="row mt-2 mb-4">
                      <div className="col-lg-4 col-md-6">
                        <div className="Week_list">
                          <div className="week_text">
                            <h4>Gregorian Range</h4>
                            <h1 className="mp-stat-range">
                              {formatDate(statsRangeStart)} - {formatDate(statsRangeEnd)}
                            </h1>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="Week_list INGREDIENTS_Cout">
                          <div className="week_text">
                            <h4>Scheduled Days</h4>
                            <h1>{summary.uniqueDays}</h1>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h1 className="title_heading mp-section-heading">Miqaat Caterer Statistics</h1>
                    {miqaatCatererFrequency.length === 0 ? (
                      <p className="mp-empty-note">No caterer data available.</p>
                    ) : (
                      <div className="mp-category-card mp-caterer-card">
                        <ul className="mp-dish-list">
                          {miqaatCatererFrequency.map((caterer) => (
                            <li
                              key={caterer.name}
                              className={`mp-dish-row ${caterer.count === 0 ? "mp-zero-count" : ""}`}
                            >
                              <span className="mp-dish-name">{caterer.name}</span>
                              <span className="mp-count-badge">{caterer.count}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </div>
      <Modal
        isOpen={!!catererRowPendingDelete}
        onRequestClose={closeCatererDeleteModal}
        style={customModal}
        contentLabel="Remove Caterer Request Modal"
        id="edit_popup"
      >
        <div className="popup_modal">
          <div className="Remove_from">
            <img src={remove} className="img-firud" />
            <h1>Delete Caterer Request</h1>
            <p>
              Do you really want to delete this caterer request? This action cannot be undone
            </p>
            {catererRowPendingDelete?.rows?.length > 0 && (
              <div className="mp-delete-modal-detail">
                <div className="mp-delete-modal-detail-date">{formatDate(catererRowPendingDelete.date)}</div>
                {catererRowPendingDelete.rows.map(({ rowKey, row }) => (
                  <div key={rowKey} className="mp-delete-modal-detail-row">
                    {row.scheduleType?.label || "FMB"} &middot; Caterer: {row.leadCaterer?.label || "No caterer"}
                  </div>
                ))}
              </div>
            )}
            <div className="Thali_page_btn_from btn-div">
              <button onClick={closeCatererDeleteModal} className="btn thali_btn">
                Cancel
              </button>
              <button onClick={confirmDeleteCatererRow} className="btn thali_btn">
                Delete
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={!!thaliMenuPendingDelete}
        onRequestClose={closeThaliMenuDeleteModal}
        style={customModal}
        contentLabel="Remove Thali Schedule Modal"
        id="edit_popup"
      >
        <div className="popup_modal">
          <div className="Remove_from">
            <img src={remove} className="img-firud" />
            <h1>Remove Thali</h1>
            <p>
              Do you really want to remove Thaali? This action cannot be undone
            </p>
            {thaliMenuPendingDelete?.entries?.length > 0 && (
              <div className="mp-delete-modal-detail">
                <div className="mp-delete-modal-detail-date">{formatDate(thaliMenuPendingDelete.date)}</div>
                {thaliMenuPendingDelete.entries.map((entry) => {
                  const dishNames = parseMenu(entry.menu).filter(Boolean);
                  return (
                    <div key={entry.id} className="mp-delete-modal-detail-row">
                      {SCHEDULE_TYPE_OPTIONS.find((o) => o.value === entry.schedule_type)?.label || entry.schedule_type}
                      {" "}&middot; {dishNames.length > 0 ? dishNames.join(", ") : "No menu set"}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="Thali_page_btn_from btn-div">
              <button onClick={closeThaliMenuDeleteModal} className="btn thali_btn">
                Cancel
              </button>
              <button onClick={confirmDeleteThaliMenu} className="btn thali_btn">
                Remove
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}